/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import auth.UserIdManager
import i18n.LocaleManager
import i18n.StringKey
import i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.TimeZone
import kotlinx.datetime.plus
import kotlinx.datetime.todayIn
import models.AdviceCard
import models.AdviceCardCategory
import models.GoalPriority
import models.SavingsGoal
import org.w3c.dom.HTMLElement
import repository.WebAdviceCardRepository
import repository.createAdviceCardRepositoryAsync
import kotlin.js.Date

/**
 * Web component for Savings Goals screen
 */
class WebSavingsGoalsComponent(
    private val navigationManager: WebNavigationManager,
    private val openCreateDialog: Boolean = false,
    // Default standard (not Pro): a forgotten wire shows the Goal Lab upsell to a
    // Pro user rather than handing a standard user the Pro feature for free.
    private val isProTier: Boolean = false,
    // Show the "PRO" pill on the flask: standard AND trialing. isProTier still
    // governs the lock, so trialing sees the pill but the Lab still opens.
    private val showProTag: Boolean = false
) {
    private val viewModel = WebSavingsGoalsViewModel()
    private var containerElement: HTMLElement? = null
    // Nav intro animation (Goals actions fade in, Budget slides right) plays once per
    // visit to the Goals page, not on every internal re-render.
    private var navIntroPlayed = false

    // Personalized Insights component (AI-generated from server)
    private var personalizedAdviceComponent: PersonalizedAdviceCardsComponent? = null

    // Advice Cards state (static financial tips)
    private var availableCards: List<AdviceCard> = emptyList()
    private var pinnedCards: List<AdviceCard> = emptyList()
    private var searchText: String = ""
    private var shuffledCardIds: List<String> = emptyList()
    private var adviceRepository: WebAdviceCardRepository? = null
    private val userIdManager = UserIdManager()
    private val userId: String get() = userIdManager.getUserId()
    private val coroutineScope = CoroutineScope(Dispatchers.Main)

    // Filtered cards based on search text
    private val filteredAvailableCards: List<AdviceCard>
        get() {
            if (searchText.isBlank()) return availableCards
            val lowercasedSearch = searchText.lowercase()
            return availableCards.filter { card ->
                card.title.lowercase().contains(lowercasedSearch) ||
                card.content.lowercase().contains(lowercasedSearch) ||
                card.category.displayName.lowercase().contains(lowercasedSearch)
            }
        }

    private val filteredPinnedCards: List<AdviceCard>
        get() {
            if (searchText.isBlank()) return pinnedCards
            val lowercasedSearch = searchText.lowercase()
            return pinnedCards.filter { card ->
                card.title.lowercase().contains(lowercasedSearch) ||
                card.content.lowercase().contains(lowercasedSearch) ||
                card.category.displayName.lowercase().contains(lowercasedSearch)
            }
        }

    init {
        viewModel.onStateChanged = {
            updateUI()
        }
        // Load advice cards
        loadAdviceCards()
    }

    private fun loadAdviceCards() {
        console.log("🔄 Web AdviceCards: Starting loadAdviceCards() for userId=$userId")
        coroutineScope.launch {
            try {
                console.log("🔄 Web AdviceCards: Creating repository...")
                val repo = createAdviceCardRepositoryAsync()
                console.log("✅ Web AdviceCards: Repository created")
                adviceRepository = repo

                // Country is auto-detected on login and stored in server user_settings.
                // The local DB doesn't have this, so we must fetch it and pass it in,
                // otherwise country-specific cards (TFSA/RRSP, 401k, etc.) are filtered out.
                val userCountry = try {
                    ApiService().getSetting("country")
                } catch (e: Exception) {
                    console.log("⚠️ Web AdviceCards: Could not fetch country setting: ${e.message}")
                    null
                }
                console.log("🌍 Web AdviceCards: Using country='$userCountry'")

                console.log("🔄 Web AdviceCards: Fetching available cards...")
                val available = repo.getAvailableCards(userId, country = userCountry)
                console.log("✅ Web AdviceCards: Got ${available.size} available cards from DB")

                console.log("🔄 Web AdviceCards: Fetching pinned cards...")
                val pinned = repo.getPinnedCards(userId)
                console.log("✅ Web AdviceCards: Got ${pinned.size} pinned cards from DB")

                // Filter out pinned from available
                var filteredAvailable = available.filter { card -> !pinned.any { it.id == card.id } }

                // Shuffle or maintain existing order
                if (shuffledCardIds.isEmpty()) {
                    // First load - shuffle the cards
                    filteredAvailable = filteredAvailable.shuffled()
                    shuffledCardIds = filteredAvailable.map { it.id }
                } else {
                    // Maintain existing shuffle order
                    filteredAvailable = filteredAvailable.sortedBy { card ->
                        shuffledCardIds.indexOf(card.id).let { if (it == -1) Int.MAX_VALUE else it }
                    }
                }

                availableCards = filteredAvailable
                pinnedCards = pinned
                console.log("📊 Web AdviceCards: Final - ${availableCards.size} available, ${pinnedCards.size} pinned")

                // Log card titles for debugging
                if (availableCards.isNotEmpty()) {
                    console.log("📝 Available card titles: ${availableCards.take(3).joinToString { it.title }}")
                }

                console.log("🔄 Web AdviceCards: Calling updateUI()...")
                updateUI()
                console.log("✅ Web AdviceCards: updateUI() completed")
            } catch (e: Exception) {
                console.error("❌ Web AdviceCards: Error loading cards: ${e.message}")
                e.printStackTrace()
            }
        }
    }

    private fun pinCard(cardId: String) {
        coroutineScope.launch {
            try {
                adviceRepository?.pinCard(userId, cardId)
                console.log("📌 Web AdviceCards: Pinned card $cardId")
                loadAdviceCards()
            } catch (e: Exception) {
                console.error("❌ Web AdviceCards: Error pinning card: ${e.message}")
            }
        }
    }

    private fun unpinCard(cardId: String) {
        coroutineScope.launch {
            try {
                adviceRepository?.unpinCard(userId, cardId)
                console.log("📌 Web AdviceCards: Unpinned card $cardId")
                loadAdviceCards()
            } catch (e: Exception) {
                console.error("❌ Web AdviceCards: Error unpinning card: ${e.message}")
            }
        }
    }

    private fun dismissCard(cardId: String) {
        coroutineScope.launch {
            try {
                adviceRepository?.dismissCard(userId, cardId)
                console.log("🗑️ Web AdviceCards: Dismissed card $cardId")
                loadAdviceCards()
            } catch (e: Exception) {
                console.error("❌ Web AdviceCards: Error dismissing card: ${e.message}")
            }
        }
    }

    fun render(): HTMLElement {
        // Start observing goals AFTER login (when this component is rendered)
        viewModel.startObserving()

        val container = document.createElement("div") as HTMLElement
        containerElement = container

        // Create personalized advice component (AI-generated insights from server)
        personalizedAdviceComponent = PersonalizedAdviceCardsComponent(
            navigationManager = navigationManager,
            onAuthError = { /* Handle auth error if needed */ },
            onShowFilteredTransactions = { category, merchants, title ->
                showFilteredTransactionsModal(category, merchants, title)
            },
            onReviewSubscriptions = {
                showSubscriptionReviewModal()
            }
        )

        // Render goals view
        val goalsView = WebGoalsListView(
            viewModel = viewModel,
            rootContainer = container,
            onOpenStudio = { goal -> openStudioForGoal(goal) },
        )

        // Render financial tips (static advice cards)
        val financialTipsHtml = renderFinancialTipsSection()

        // Responsive layout
        val isMobile = ResponsiveUtils.isMobile()
        val padding = if (isMobile) "1rem" else "2rem"

        // On desktop the create button lives here, top-right of the goals section,
        // rather than in the nav — "+ Ajouter un objectif" is wide enough to shove
        // the Budget tab off-balance. Mobile keeps it in its compact top bar.
        val addGoalButtonHtml =
            if (isMobile) {
                ""
            } else {
                val bg = if (WebTheme.current.isDark) "#00d9ff" else WebTheme.current.accentStrong
                val fg = if (WebTheme.current.isDark) "#0a0a0a" else "#ffffff"
                """<button id="addGoalButton" data-guide="add-goal-button" style="background: $bg; color: $fg; border: none; border-radius: 6px; padding: 8px 16px; cursor: pointer; font-size: 13px; font-weight: 600; transition: all 0.2s;">+ ${StringKey.GOALS_ADD_NEW.tr()}</button>"""
            }

        container.innerHTML =
            """
            <div style="display: flex; flex-direction: column; min-height: 100dvh; background: transparent; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
                <!-- Top Navigation Bar -->
                ${renderTopNav(isMobile)}

                <!-- Mobile Menu Overlay (hidden by default) -->
                ${MarketingMenu.mobileOverlayHtml()}

                <!-- Content -->
                <div style="flex: 1; padding: $padding;">
                    <div id="goalsContainer" style="max-width: 1400px; margin: 0 auto;">
                        <!-- 1. Savings Goals (now first — users come here for these) -->
                        <div style="margin-bottom: 1.5rem;">
                            <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 1rem;">
                                <h2 style="color: ${WebTheme.current.textPrimary}; font-size: 1.25rem; font-weight: 600; margin: 0;">${StringKey.GOALS_TITLE.tr()}</h2>
                                $addGoalButtonHtml
                            </div>
                            <style>
                                @keyframes goalModalIn {
                                    0% { transform: scale(0.85); opacity: 0; }
                                    100% { transform: scale(1); opacity: 1; }
                                }
                                @keyframes goalOverlayIn {
                                    0% { opacity: 0; }
                                    100% { opacity: 1; }
                                }
                                .goal-modal-overlay {
                                    animation: goalOverlayIn 0.3s ease forwards;
                                }
                                .goal-modal-card {
                                    animation: goalModalIn 0.4s cubic-bezier(0.22, 1, 0.36, 1) forwards;
                                }
                            </style>
                            <div id="goalsListContainer" style="
                                display: grid;
                                grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
                                gap: 12px;
                            ">
                                ${goalsView.render()}
                            </div>
                        </div>

                        <!-- 2. Personalized Insights (AI-generated) — between Goals and Tips -->
                        <div id="personalizedInsightsContainer" data-guide="personalized-insights"></div>

                        <!-- 3. Financial Tips (static advice cards with search) -->
                        <div id="financialTipsContainer">
                            $financialTipsHtml
                        </div>
                    </div>
                </div>

                <!-- Add Goal Dialog -->
                <div id="addGoalDialog" style="display: none; position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 1000; align-items: center; justify-content: center;">
                </div>
            </div>
            """.trimIndent()

        // Render personalized insights into its container
        val insightsContainer = container.querySelector("#personalizedInsightsContainer") as? HTMLElement
        insightsContainer?.let {
            val insightsElement = personalizedAdviceComponent?.render()
            insightsElement?.let { el -> it.appendChild(el) }
        }

        // Logo click - navigate to Landing page
        container.querySelector("#goals-logo")?.addEventListener("click", {
            navigationManager.navigateTo(Screen.Landing)
        })

        // Navigation event listeners (desktop)
        container.querySelector("#nav-activity")?.addEventListener("click", {
            navigationManager.switchToTab(ui.AppTabType.TRANSACTIONS)
        })
        container.querySelector("#nav-dashboard")?.addEventListener("click", {
            navigationManager.switchToTab(ui.AppTabType.DASHBOARD)
        })
        container.querySelector("#nav-goals")?.addEventListener("click", {
            navigationManager.switchToTab(ui.AppTabType.GOALS)
        })
        container.querySelector("#nav-budget")?.addEventListener("click", {
            navigationManager.switchToTab(ui.AppTabType.BUDGET_PLANNING)
        })

        // Nav intro: the Goals actions fade in while Budget slides right to make room.
        // Budget's travel equals the width the buttons actually occupy (varies by locale),
        // so measure once here and hand it to CSS as --wiz3r-slide; the animation itself
        // is pure transform/opacity. Runs only on the first render of this page visit, so
        // internal refreshes (e.g. after saving a goal) don't replay it.
        if (!navIntroPlayed) {
            navIntroPlayed = true
            val goalsTab = container.querySelector("#nav-goals") as? org.w3c.dom.HTMLElement
            val budgetTab = container.querySelector("#nav-budget") as? org.w3c.dom.HTMLElement
            if (goalsTab != null && budgetTab != null) {
                // Distance Budget travels = the space now sitting between the end of the
                // Goals tab and Budget's left edge, minus the row's normal gap. Derived
                // from live positions rather than summing button widths, so it stays exact
                // regardless of locale-dependent label widths and the grouping margins.
                val navGap = 8
                val slidePx = budgetTab.offsetLeft - (goalsTab.offsetLeft + goalsTab.offsetWidth) - navGap
                if (slidePx > 0) {
                    budgetTab.style.setProperty("--wiz3r-slide", "${slidePx}px")
                    budgetTab.classList.add("wiz3r-budget-slide")
                }
            }
        }
        // Language toggle - quick switch between languages
        container.querySelector("#nav-language")?.addEventListener("click", {
            LocaleManager.toggleLocale()
            window.location.reload()
        })

        // "How Wiz3r works" button → modal overlay
        container.querySelector("#nav-guide")?.addEventListener("click", {
            showGuideModal()
        })

        // Tour button
        GuideOverlay.ensureStylesInstalled()
        container.querySelector("#nav-tour")?.addEventListener("click", {
            // Tag the first goal card + first pin button just before the tour reads
            // them — content is rendered async so these may not exist at page-render time.
            (container.querySelector(".goalCard") as? HTMLElement)?.setAttribute("data-guide", "goal-card")
            (container.querySelector(".pinBtn") as? HTMLElement)?.setAttribute("data-guide", "pin-icon")
            // Detect whether the user is currently seeing Wiz 3R cards (any
            // card with a narrative). The PersonalizedAdviceCardsComponent
            // marks its container with data-wiz3r-active="true" when so.
            val wiz3rActive = document.querySelector("[data-wiz3r-active='true']") != null
            GuideOverlay(guide.GuideScreen.GOALS, wiz3rEnabled = wiz3rActive).start()
            (container.querySelector("#nav-tour") as? HTMLElement)?.classList?.remove("wiz3r-guide-halo")
        })

        // Marketing-capture demo mode (?demo=1): auto-trigger 'i' click.
        autoStartTourIfDemoMode("#nav-tour")

        // Mobile menu handlers
        val mobileMenuOverlay = container.querySelector("#mobileMenuOverlay") as? HTMLElement
        val mobileMenuBackdrop = container.querySelector("#mobileMenuBackdrop") as? HTMLElement
        container.querySelector("#hamburgerMenu")?.addEventListener("click", {
            mobileMenuOverlay?.style?.display = "block"
            mobileMenuBackdrop?.style?.display = "block"
        })
        MarketingMenu.attachMobileEventListeners(
            container = container,
            onNavigate = { page ->
                if (page == "guide") {
                    showGuideModal()
                    return@attachMobileEventListeners
                }
                val screen = when (page) {
                    "team" -> Screen.Team
                    "about" -> Screen.About
                    "contact" -> Screen.Contact
                    "subscribe" -> Screen.Subscribe()
                    "privacy" -> Screen.Privacy
                    "terms" -> Screen.Terms
                    "security" -> Screen.Security
                    else -> Screen.Landing
                }
                navigationManager.navigateTo(screen)
            },
            onAppNavigate = { tab ->
                when (tab) {
                    "transactions" -> navigationManager.switchToTab(ui.AppTabType.TRANSACTIONS)
                    "dashboard" -> navigationManager.switchToTab(ui.AppTabType.DASHBOARD)
                    "goals" -> navigationManager.switchToTab(ui.AppTabType.GOALS)
                    "budget" -> navigationManager.switchToTab(ui.AppTabType.BUDGET_PLANNING)
                    "settings" -> navigationManager.switchToTab(ui.AppTabType.SETTINGS)
                    // An actual logout — see WebDashboardRedesigned: navigating to
                    // Landing left the stored token intact, so the visitor was
                    // never signed out.
                    "logout" -> navigationManager.logout()
                }
            }
        )

        // Create goal → simple type picker → simple form. No studio-profile
        // gate: the simple form doesn't need it (the Lab does, and gates itself).
        container.querySelector("#addGoalButton")?.addEventListener("click", {
            WebGoalDialogHelper.showGoalTypePicker(container, viewModel)
        })

        container.querySelector("#emptyStateAddButton")?.addEventListener("click", {
            WebGoalDialogHelper.showGoalTypePicker(container, viewModel)
        })

        // Goal Lab → the standalone scenario explorer. It's a Pro feature: a
        // standard user gets routed to the paywall instead of opening it. For
        // Pro/trialing, it's gated only on the studio profile stepper (it needs
        // the profile to project timelines).
        container.querySelector("#goalLabButton")?.addEventListener("click", {
            if (!isProTier) {
                navigationManager.navigateTo(Screen.Subscribe())
            } else {
                gateWithStudioProfile {
                    WebGoalDialogHelper.openLab(container, viewModel)
                }
            }
        })

        // If openCreateDialog is true, automatically show the create dialog
        if (openCreateDialog) {
            WebGoalDialogHelper.showGoalTypePicker(container, viewModel)
        }

        // CRITICAL: Attach listeners to the initially rendered goals
        goalsView.attachListeners()
        attachFinancialTipsListeners(container)

        // Scroll listener for navbar shrink — only runs while this component is in the DOM
        window.addEventListener("scroll", { _ ->
            if (!document.body!!.contains(container)) return@addEventListener
            val scrollY = window.scrollY
            val logo = document.getElementById("goals-logo") as? org.w3c.dom.HTMLElement
            val navbar = logo?.parentElement as? org.w3c.dom.HTMLElement
            val progress = (scrollY / 80.0).coerceIn(0.0, 1.0)
            val vPad = (16 - (8 * progress)).toInt()
            val logoH = (56 - (28 * progress)).toInt()
            navbar?.style?.setProperty("padding", "${vPad}px 32px")
            logo?.style?.setProperty("height", "${logoH}px")
        })

        return container
    }

    private fun renderTopNav(isMobile: Boolean): String {
        val t = WebTheme.current
        val logoSrc = if (t.isDark) "/wiz3r_logo.png?v=3" else "/wiz3r_on_white.png?v=2"
        val addBtnBg = if (t.isDark) "#00d9ff" else t.accentStrong
        val addBtnFg = if (t.isDark) "#0a0a0a" else "#ffffff"
        val tourHaloClass = if (!GuideOverlay.hasSeen(guide.GuideScreen.GOALS)) "wiz3r-guide-halo" else ""
        val tourTooltip = StringKey.TOUR_OPEN_TOOLTIP.tr()
        // Goal Lab is Pro — tag the flask for standard AND trialing users.
        val labProPill = if (showProTag) {
            """<span style="background: ${t.proBadge}; color: ${t.proBadgeText}; font-size: 8px; font-weight: 700; padding: 1px 4px; border-radius: 3px; letter-spacing: 0.3px; margin-left: 2px;">PRO</span>"""
        } else ""
        return if (isMobile) {
            // Mobile navigation with hamburger menu
            """
            <div style="display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; background: ${t.navBackground}; border-bottom: 1px solid ${t.border}; position: sticky; top: 0; z-index: 100;">
                <img id="goals-logo" src="$logoSrc" alt="wiz≡r" style="height: 32px; cursor: pointer;" title="${StringKey.TOOLTIP_GO_HOME.tr()}">
                <div style="display: flex; align-items: center; gap: 8px;">
                    <button id="goalLabButton" title="${StringKey.LAB_TITLE.tr()}" style="display:inline-flex; align-items:center; gap:5px; background: transparent; color: ${t.accent}; border: 1px solid ${t.border}; border-radius: 6px; padding: 6px 10px; cursor: pointer; font-size: 12px; font-weight: 600;"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2v6a2 2 0 0 0 .245.96l5.51 10.08A2 2 0 0 1 18 22H6a2 2 0 0 1-1.755-2.96l5.51-10.08A2 2 0 0 0 10 8V2"/><path d="M6.453 15h11.094"/><path d="M8.5 2h7"/></svg>${StringKey.LAB_NAV_BUTTON.tr()}${labProPill}</button>
                    <button id="addGoalButton" data-guide="add-goal-button" style="background: $addBtnBg; color: $addBtnFg; border: none; border-radius: 6px; padding: 6px 12px; cursor: pointer; font-size: 12px; font-weight: 600;">
                        + ${StringKey.GOALS_ADD_NEW.tr()}
                    </button>
                    <span id="nav-tour" class="$tourHaloClass" style="width: 26px; height: 26px; border-radius: 50%; background: ${t.navBackground}; color: ${t.accent}; border: 1px solid ${t.border}; display: inline-flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 700; cursor: pointer; font-family: serif; user-select: none;" title="$tourTooltip">i</span>
                    <span id="nav-language" style="padding: 4px 8px; border-radius: 4px; background: ${t.navBackground}; color: ${t.accent}; font-size: 12px; font-weight: 600; cursor: pointer; border: 1px solid ${t.border};" title="${StringKey.TOOLTIP_CHANGE_LANGUAGE.tr()}">${languageToggleLabel()}</span>
                    <button id="hamburgerMenu" style="background: none; border: none; color: ${t.textPrimary}; font-size: 24px; cursor: pointer; padding: 4px;">☰</button>
                </div>
            </div>
            """
        } else {
            // Desktop navigation
            """
            <div style="display: flex; align-items: center; padding: 16px 32px; background: ${t.navBackground}; border-bottom: 1px solid ${t.border}; position: fixed; top: 0; left: 0; right: 0; z-index: 100; transition: padding 0.25s ease;">
                <img id="goals-logo" src="$logoSrc" alt="wiz≡r" style="height: 56px; cursor: pointer; margin-right: 40px; transition: height 0.25s ease;" title="${StringKey.TOOLTIP_GO_HOME.tr()}">
                <div style="display: flex; gap: 8px; flex: 1; align-items: center;">
                    <span id="nav-activity" style="padding: 8px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; background: transparent; color: ${t.navText};">${StringKey.NAV_TRANSACTIONS.tr()}</span>
                    <span id="nav-dashboard" style="padding: 8px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; background: transparent; color: ${t.navText};">${StringKey.NAV_DASHBOARD.tr()}</span>
                    <span id="nav-goals" style="padding: 8px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; background: ${t.navBackground}; color: ${t.textPrimary};">${StringKey.NAV_GOALS.tr()}</span>
                    <!-- The Lab button sits right after the active Goals tab, so it reads
                         as belonging to Goals; Budget slides right to make room. (Create
                         Goal lives above the goals grid instead — its label is wide enough
                         in French to push Budget off-balance here.)
                         Intro animation is opacity/transform only (compositor-driven, no
                         per-frame layout); Budget's slide distance is measured once after
                         render into --wiz3r-slide. Honors prefers-reduced-motion. -->
                    <style>
                        @keyframes wiz3rGoalsActionIn { from { opacity: 0; transform: translateX(-10px); } to { opacity: 1; transform: none; } }
                        @keyframes wiz3rBudgetSlide { from { transform: translateX(calc(-1 * var(--wiz3r-slide, 0px))); } to { transform: translateX(0); } }
                        .wiz3r-goals-action { animation: wiz3rGoalsActionIn 260ms cubic-bezier(.22,.61,.36,1) both; }
                        #nav-budget.wiz3r-budget-slide { animation: wiz3rBudgetSlide 300ms cubic-bezier(.22,.61,.36,1) both; }
                        @media (prefers-reduced-motion: reduce) {
                            .wiz3r-goals-action, #nav-budget.wiz3r-budget-slide { animation: none !important; }
                        }
                    </style>
                    <button id="goalLabButton" class="wiz3r-goals-action" title="${StringKey.LAB_TITLE.tr()}" style="display:inline-flex; align-items:center; gap:6px; margin-left: -6px; background: transparent; color: ${t.accent}; border: 1px solid ${t.border}; border-radius: 6px; padding: 8px 14px; cursor: pointer; font-size: 13px; font-weight: 600; transition: all 0.2s;"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2v6a2 2 0 0 0 .245.96l5.51 10.08A2 2 0 0 1 18 22H6a2 2 0 0 1-1.755-2.96l5.51-10.08A2 2 0 0 0 10 8V2"/><path d="M6.453 15h11.094"/><path d="M8.5 2h7"/></svg>${StringKey.LAB_NAV_BUTTON.tr()}${labProPill}</button>
                    <span id="nav-budget" style="padding: 8px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; background: transparent; color: ${t.navText}; margin-left: 32px;">${StringKey.NAV_BUDGET.tr()}</span>
                </div>
                <div style="display: flex; align-items: center; gap: 12px;">
                    <span id="nav-tour" class="$tourHaloClass" style="width: 28px; height: 28px; border-radius: 50%; background: ${t.navBackground}; color: ${t.accent}; border: 1px solid ${t.border}; display: inline-flex; align-items: center; justify-content: center; font-size: 15px; font-weight: 700; cursor: pointer; font-family: serif; user-select: none;" title="$tourTooltip">i</span>
                    <span id="nav-language" style="padding: 4px 10px; border-radius: 4px; background: ${t.navBackground}; color: ${t.accent}; font-size: 12px; font-weight: 600; cursor: pointer; border: 1px solid ${t.border}; transition: all 0.2s;" title="${StringKey.TOOLTIP_CHANGE_LANGUAGE.tr()}">${languageToggleLabel()}</span>
                    <span id="nav-guide" style="width: 32px; height: 32px; border-radius: 50%; background: ${t.navBackground}; display: flex; align-items: center; justify-content: center; font-size: 14px; cursor: pointer; border: 1px solid ${t.border};" title="${StringKey.TOOLTIP_HOW_IT_WORKS.tr()}">❓</span>
                    <span id="nav-settings" style="width: 32px; height: 32px; border-radius: 50%; background: ${t.navBackground}; display: flex; align-items: center; justify-content: center; font-size: 16px; cursor: pointer; border: 1px solid ${t.border};">&#9776;</span>
                </div>
            </div>
            <div style="height: 80px;"></div>
            """
        }
    }

    private fun renderFinancialTipsSection(): String {
        // Only show section if there are any cards
        if (availableCards.isEmpty() && pinnedCards.isEmpty()) {
            return ""
        }

        val t = WebTheme.current
        // Search bar HTML
        val searchBarHtml = """
            <div style="margin-bottom: 12px;" data-guide="tips-search">
                <div style="display: flex; align-items: center; background: ${if (t.isDark) "rgba(255,255,255,0.1)" else t.card}; border: 1px solid ${t.border}; border-radius: 10px; padding: 10px 12px;">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="${t.textSecondary}" stroke-width="2" style="flex-shrink: 0;">
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.35-4.35"></path>
                    </svg>
                    <input id="adviceSearchInput" type="text" placeholder="${StringKey.TIPS_SEARCH_PLACEHOLDER.tr()}" value="${searchText}"
                        style="flex: 1; background: transparent; border: none; outline: none; color: ${t.textPrimary}; font-size: 14px; margin-left: 10px;"
                    />
                    ${if (searchText.isNotEmpty()) """
                        <button id="clearSearchBtn" style="background: none; border: none; cursor: pointer; padding: 4px;">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${t.textSecondary}" stroke-width="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <path d="m15 9-6 6"></path>
                                <path d="m9 9 6 6"></path>
                            </svg>
                        </button>
                    """ else ""}
                </div>
            </div>
        """.trimIndent()

        val pinnedHtml = if (filteredPinnedCards.isNotEmpty()) renderPinnedCardsSection() else ""
        val carouselHtml = if (filteredAvailableCards.isNotEmpty()) renderAdviceCardsCarousel() else ""

        return """
            <div style="margin-top: 1.5rem;">
                <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 12px;">
                    <span style="color: #FFC107;">💡</span>
                    <span style="color: ${t.textPrimary}; font-size: 1.25rem; font-weight: 600;">${StringKey.TIPS_TITLE.tr()}</span>
                </div>
                $searchBarHtml
                $pinnedHtml
                $carouselHtml
            </div>
        """.trimIndent()
    }

    private fun renderPinnedCardsSection(): String {
        val cardsHtml = filteredPinnedCards.joinToString("") { card ->
            val color = card.category.color
            """
            <div class="pinnedCard" data-card-id="${card.id}" style="flex: 1; min-width: 140px; max-width: 180px; height: 90px; padding: 10px; border-radius: 12px; background: linear-gradient(135deg, $color, ${color}99); display: flex; flex-direction: column; justify-content: space-between; cursor: pointer; transition: transform 0.2s, box-shadow 0.2s;"
                onmouseover="this.style.transform='scale(1.02)'; this.style.boxShadow='0 8px 24px rgba(0,0,0,0.3)';"
                onmouseout="this.style.transform='scale(1)'; this.style.boxShadow='none';">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                    <span style="font-size: 12px; color: rgba(255,255,255,0.9);">${getCategoryIcon(card.category)}</span>
                    <button class="unpinBtn" data-card-id="${card.id}" style="background: none; border: none; cursor: pointer; color: rgba(255,255,255,0.8); font-size: 12px; padding: 2px;">✕</button>
                </div>
                <div>
                    <div style="font-size: 12px; font-weight: 700; color: #ffffff; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${card.title}</div>
                    <div style="font-size: 10px; color: rgba(255,255,255,0.8); overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;">${card.content}</div>
                </div>
            </div>
            """.trimIndent()
        }

        return """
        <div style="margin-bottom: 1.5rem;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 12px;">
                <span style="color: #FF9800;">📌</span>
                <span style="color: ${WebTheme.current.textPrimary}; font-size: 1rem; font-weight: 600;">${StringKey.TIPS_PINNED.tr()}</span>
            </div>
            <div style="display: flex; gap: 12px; flex-wrap: wrap;">
                $cardsHtml
            </div>
        </div>
        """.trimIndent()
    }

    private fun renderAdviceCardsCarousel(): String {
        val cardsHtml = filteredAvailableCards.joinToString("") { card ->
            val color = card.category.color
            """
            <div class="adviceCard" data-card-id="${card.id}" style="flex-shrink: 0; width: 200px; height: 150px; padding: 12px; border-radius: 16px; background: linear-gradient(135deg, $color, ${color}99); display: flex; flex-direction: column; justify-content: space-between; box-shadow: 0 4px 12px ${color}4D; cursor: pointer; transition: transform 0.2s, box-shadow 0.2s;"
                onmouseover="this.style.transform='scale(1.03)'; this.style.boxShadow='0 8px 24px ${color}66';"
                onmouseout="this.style.transform='scale(1)'; this.style.boxShadow='0 4px 12px ${color}4D';">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                    <div style="display: flex; align-items: center; gap: 4px;">
                        <span style="font-size: 12px; color: rgba(255,255,255,0.9);">${getCategoryIcon(card.category)}</span>
                        <span style="font-size: 11px; font-weight: 600; color: rgba(255,255,255,0.9);">${card.category.displayName}</span>
                    </div>
                    <div style="display: flex; gap: 4px;">
                        <button class="pinBtn" data-card-id="${card.id}" style="background: none; border: none; cursor: pointer; color: rgba(255,255,255,0.8); font-size: 14px; padding: 2px;">📌</button>
                        <button class="dismissBtn" data-card-id="${card.id}" style="background: none; border: none; cursor: pointer; color: rgba(255,255,255,0.8); font-size: 14px; padding: 2px;">✕</button>
                    </div>
                </div>
                <div>
                    <div style="font-size: 14px; font-weight: 700; color: #ffffff; margin-bottom: 4px; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;">${card.title}</div>
                    <div style="font-size: 12px; color: rgba(255,255,255,0.85); overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical;">${card.content}</div>
                </div>
            </div>
            """.trimIndent()
        }

        return """
        <div style="margin-bottom: 1rem;">
            <div style="display: flex; gap: 16px; overflow-x: auto; padding-bottom: 8px; scrollbar-width: thin; scrollbar-color: ${WebTheme.current.border} ${WebTheme.current.background};">
                $cardsHtml
            </div>
        </div>
        """.trimIndent()
    }

    private fun getCategoryIcon(category: AdviceCardCategory): String = when (category) {
        AdviceCardCategory.SAVINGS_TIPS -> "📊"
        AdviceCardCategory.GOAL_SETTING -> "🎯"
        AdviceCardCategory.ACCOUNTS -> "🏦"
        AdviceCardCategory.ENCOURAGEMENT -> "⭐"
        AdviceCardCategory.BEHAVIORAL -> "🧠"
    }

    private fun attachFinancialTipsListeners(container: HTMLElement) {
        // Search input handler
        val searchInput = container.querySelector("#adviceSearchInput") as? org.w3c.dom.HTMLInputElement
        searchInput?.addEventListener("input", { _ ->
            searchText = searchInput.value
            updateUI()
        })

        // Clear search button handler
        val clearBtn = container.querySelector("#clearSearchBtn")
        clearBtn?.addEventListener("click", {
            searchText = ""
            updateUI()
        })

        // Card click handlers (for expansion)
        val adviceCards = container.querySelectorAll(".adviceCard")
        for (i in 0 until adviceCards.length) {
            val card = adviceCards.item(i) as? HTMLElement
            card?.addEventListener("click", { event ->
                val target = event.target as? HTMLElement
                // Don't open modal if clicking on a button
                if (target?.classList?.contains("pinBtn") == true ||
                    target?.classList?.contains("dismissBtn") == true) {
                    return@addEventListener
                }
                val cardId = card.getAttribute("data-card-id")
                cardId?.let { id ->
                    val adviceCard = availableCards.find { it.id == id }
                    adviceCard?.let { showExpandedCardModal(it, container) }
                }
            })
        }

        // Pinned card click handlers
        val pinnedCardElements = container.querySelectorAll(".pinnedCard")
        for (i in 0 until pinnedCardElements.length) {
            val card = pinnedCardElements.item(i) as? HTMLElement
            card?.addEventListener("click", { event ->
                val target = event.target as? HTMLElement
                // Don't open modal if clicking on unpin button
                if (target?.classList?.contains("unpinBtn") == true) {
                    return@addEventListener
                }
                val cardId = card.getAttribute("data-card-id")
                cardId?.let { id ->
                    val adviceCard = pinnedCards.find { it.id == id }
                    adviceCard?.let { showExpandedCardModal(it, container) }
                }
            })
        }

        // Pin buttons
        val pinButtons = container.querySelectorAll(".pinBtn")
        for (i in 0 until pinButtons.length) {
            val btn = pinButtons.item(i) as? HTMLElement
            btn?.addEventListener("click", { event ->
                event.stopPropagation()
                val cardId = btn.getAttribute("data-card-id")
                cardId?.let { pinCard(it) }
            })
        }

        // Unpin buttons
        val unpinButtons = container.querySelectorAll(".unpinBtn")
        for (i in 0 until unpinButtons.length) {
            val btn = unpinButtons.item(i) as? HTMLElement
            btn?.addEventListener("click", { event ->
                event.stopPropagation()
                val cardId = btn.getAttribute("data-card-id")
                cardId?.let { unpinCard(it) }
            })
        }

        // Dismiss buttons
        val dismissButtons = container.querySelectorAll(".dismissBtn")
        for (i in 0 until dismissButtons.length) {
            val btn = dismissButtons.item(i) as? HTMLElement
            btn?.addEventListener("click", { event ->
                event.stopPropagation()
                val cardId = btn.getAttribute("data-card-id")
                cardId?.let { dismissCard(it) }
            })
        }
    }

    private fun showExpandedCardModal(card: AdviceCard, container: HTMLElement) {
        val color = card.category.color
        val illustration = getIllustrationSvg(card.id)
        val whyItWorks = getWhyItWorks(card.id)
        val examples = getExamplesForCard(card.id)

        val tm = WebTheme.current
        val examplesHtml = if (examples.isNotEmpty()) {
            val rows = examples.joinToString("") { (label, value) ->
                """
                <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid ${tm.border};">
                    <span style="color: ${tm.textSecondary};">$label</span>
                    <span style="color: ${tm.textPrimary}; font-weight: 600;">$value</span>
                </div>
                """.trimIndent()
            }
            """
            <div style="
                background: ${if (tm.isDark) "rgba(255,255,255,0.05)" else tm.background};
                border-radius: 12px;
                padding: 16px;
                margin-bottom: 20px;
            ">
                <div style="color: $color; font-size: 0.75rem; font-weight: 700; margin-bottom: 10px; letter-spacing: 0.5px;">
                    ${StringKey.TIPS_EXAMPLE.tr()}
                </div>
                $rows
            </div>
            """.trimIndent()
        } else ""

        val modalHtml = """
            <div id="adviceCardModal" style="
                position: fixed; inset: 0;
                background: ${if (tm.isDark) "rgba(0,0,0,0.85)" else "rgba(0,0,0,0.3)"};
                display: flex; align-items: center; justify-content: center;
                z-index: 2000;
                animation: fadeIn 0.2s ease-out;
            ">
                <style>
                    @keyframes fadeIn {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                    @keyframes scaleIn {
                        from { opacity: 0; transform: scale(0.85); }
                        to { opacity: 1; transform: scale(1); }
                    }
                    @keyframes drawLine {
                        to { stroke-dashoffset: 0; }
                    }
                    @keyframes pieSliceIn {
                        from { opacity: 0; transform: scale(0.8); }
                        to { opacity: 1; transform: scale(1); }
                    }
                    @keyframes barGrow {
                        from { transform: scaleY(0); }
                        to { transform: scaleY(1); }
                    }
                </style>
                <div style="
                    max-width: 580px; width: 90%;
                    background: ${tm.card};
                    border-radius: 20px;
                    overflow: hidden;
                    animation: scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: ${if (tm.isDark) "none" else "0 20px 60px rgba(0,0,0,0.15)"};
                ">
                    <!-- Header with subtle gradient -->
                    <div style="
                        background: linear-gradient(135deg, ${color}4D 0%, ${tm.card} 100%);
                        padding: 28px 24px;
                        display: flex;
                        align-items: center;
                        gap: 16px;
                        border-bottom: 1px solid ${color}26;
                    ">
                        <div style="flex-shrink: 0;">$illustration</div>
                        <div style="flex: 1;">
                            <div style="color: $color; font-size: 11px; font-weight: 600; letter-spacing: 0.5px; margin-bottom: 4px;">
                                ${card.category.name.uppercase().replace("_", " ")}
                            </div>
                            <h2 style="color: ${tm.textPrimary}; font-size: 1.25rem; font-weight: 700; margin: 0; line-height: 1.3;">
                                ${card.title}
                            </h2>
                        </div>
                    </div>

                    <!-- Content -->
                    <div style="padding: 20px;">
                        <!-- Description -->
                        <p style="color: ${tm.textSecondary}; font-size: 0.9rem; line-height: 1.6; margin: 0 0 20px;">
                            ${card.content}
                        </p>

                        <!-- Why it works section -->
                        <div style="
                            background: ${if (tm.isDark) "rgba(255,255,255,0.03)" else tm.background};
                            border-left: 3px solid $color;
                            border-radius: 0 8px 8px 0;
                            padding: 12px 16px;
                            margin-bottom: 20px;
                        ">
                            <div style="color: $color; font-size: 0.7rem; font-weight: 700; margin-bottom: 6px; letter-spacing: 0.5px;">
                                ${StringKey.TIPS_WHY_IT_WORKS.tr()}
                            </div>
                            <p style="
                                color: ${tm.textPrimary};
                                font-size: 0.9rem;
                                line-height: 1.5;
                                margin: 0;
                            ">$whyItWorks</p>
                        </div>

                        $examplesHtml

                        <!-- A "RESOURCES — curated links & tools coming soon"
                             placeholder stood here, advertising a feature that was
                             never built. Removed on all three platforms; see
                             SavingsGoalsViewKMP.swift for the reasoning. -->

                        <!-- Got it button -->
                        <div style="display: flex; justify-content: center;">
                            <button id="closeExpandedCardBtn" style="
                                background: ${color}1F;
                                color: $color;
                                border: 1px solid ${color}40;
                                border-radius: 24px;
                                padding: 10px 28px;
                                font-size: 0.9rem;
                                font-weight: 600;
                                cursor: pointer;
                                transition: all 0.2s;
                            "
                            onmouseover="this.style.background='${color}33'"
                            onmouseout="this.style.background='${color}1F'">
                                ${StringKey.TIPS_GOT_IT.tr()}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        """.trimIndent()

        // Create modal element
        val modalWrapper = document.createElement("div") as HTMLElement
        modalWrapper.innerHTML = modalHtml
        document.body?.appendChild(modalWrapper)

        // Close button handler
        val closeBtn = document.querySelector("#closeExpandedCardBtn")
        closeBtn?.addEventListener("click", {
            modalWrapper.remove()
        })

        // Click outside to close
        val modal = document.querySelector("#adviceCardModal")
        modal?.addEventListener("click", { event ->
            if (event.target == modal) {
                modalWrapper.remove()
            }
        })

        // Escape key to close - use a simple approach
        document.addEventListener("keydown", { event ->
            val keyEvent = event.asDynamic()
            if (keyEvent.key == "Escape" && document.querySelector("#adviceCardModal") != null) {
                modalWrapper.remove()
            }
        })
    }

    // Compact illustrations for side-by-side header layout
    private fun getIllustrationSvg(cardId: String): String {
        return when {
            cardId.contains("50-30-20") -> getCompactPieChartSvg()
            cardId.contains("compound") -> getCompactGrowthSvg()
            cardId.contains("small-wins") || cardId.contains("coffee") -> getCompactBarsSvg()
            cardId.contains("emergency") -> getCompactShieldSvg()
            cardId.contains("automate") -> getCompactAutomationSvg()
            else -> getCompactEmojiSvg(getEmojiForCardId(cardId))
        }
    }

    private fun getCompactPieChartSvg(): String = """
        <svg width="56" height="56" viewBox="0 0 56 56" style="animation: pieSliceIn 0.6s ease-out;">
            <circle cx="28" cy="28" r="22" fill="none" stroke="#2196F3" stroke-width="8"
                stroke-dasharray="69 138" stroke-dashoffset="-34.5"
                style="transform: rotate(-90deg); transform-origin: center;"/>
            <circle cx="28" cy="28" r="22" fill="none" stroke="#FF9800" stroke-width="8"
                stroke-dasharray="41.4 138" stroke-dashoffset="-103.5"
                style="transform: rotate(-90deg); transform-origin: center;"/>
            <circle cx="28" cy="28" r="22" fill="none" stroke="#4CAF50" stroke-width="8"
                stroke-dasharray="27.6 138" stroke-dashoffset="6.9"
                style="transform: rotate(-90deg); transform-origin: center;"/>
        </svg>
    """.trimIndent()

    private fun getCompactGrowthSvg(): String = """
        <svg width="56" height="56" viewBox="0 0 56 56">
            <path d="M 8 48 Q 20 46, 28 38 Q 36 28, 48 8"
                fill="none" stroke="#4CAF50" stroke-width="3" stroke-linecap="round"
                style="stroke-dasharray: 80; stroke-dashoffset: 80; animation: drawLine 1s ease-out forwards;"/>
            <circle cx="48" cy="8" r="4" fill="#4CAF50" style="opacity: 0; animation: pieSliceIn 0.3s ease-out 0.8s forwards;"/>
        </svg>
    """.trimIndent()

    private fun getCompactBarsSvg(): String = """
        <div style="display: flex; gap: 3px; align-items: flex-end; height: 56px;">
            <div style="width: 10px; height: 16px; background: rgba(76,175,80,0.5); border-radius: 3px 3px 0 0; transform-origin: bottom; animation: barGrow 0.4s ease-out;"></div>
            <div style="width: 10px; height: 28px; background: rgba(76,175,80,0.7); border-radius: 3px 3px 0 0; transform-origin: bottom; animation: barGrow 0.5s ease-out;"></div>
            <div style="width: 10px; height: 42px; background: rgba(76,175,80,0.85); border-radius: 3px 3px 0 0; transform-origin: bottom; animation: barGrow 0.6s ease-out;"></div>
            <div style="width: 10px; height: 56px; background: #4CAF50; border-radius: 3px 3px 0 0; transform-origin: bottom; animation: barGrow 0.7s ease-out;"></div>
        </div>
    """.trimIndent()

    private fun getCompactShieldSvg(): String = """
        <div style="font-size: 48px; line-height: 1;">🛡️</div>
    """.trimIndent()

    private fun getCompactAutomationSvg(): String = """
        <div style="display: flex; align-items: center; gap: 6px;">
            <div style="font-size: 24px;">💵</div>
            <svg width="24" height="16" viewBox="0 0 24 16">
                <line x1="0" y1="8" x2="16" y2="8" stroke="#fff" stroke-width="2" stroke-linecap="round"
                    style="stroke-dasharray: 16; stroke-dashoffset: 16; animation: drawLine 0.5s ease-out forwards;"/>
                <polyline points="12,4 18,8 12,12" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round"
                    style="stroke-dasharray: 16; stroke-dashoffset: 16; animation: drawLine 0.5s ease-out 0.3s forwards;"/>
            </svg>
            <div style="font-size: 24px;">🏦</div>
        </div>
    """.trimIndent()

    private fun getCompactEmojiSvg(emoji: String): String = """
        <div style="font-size: 48px; line-height: 1;">$emoji</div>
    """.trimIndent()

    private fun getEmojiForCardId(cardId: String): String = when {
        cardId.contains("50-30-20") -> "📊"
        cardId.contains("automate") -> "🤖"
        cardId.contains("24-hour") || cardId.contains("wait") -> "⏰"
        cardId.contains("compound") -> "📈"
        cardId.contains("small-wins") || cardId.contains("coffee") -> "☕"
        cardId.contains("emergency") -> "🛡️"
        cardId.contains("account") || cardId.contains("roth") || cardId.contains("401k") || cardId.contains("tfsa") || cardId.contains("rrsp") || cardId.contains("fhsa") -> "🏦"
        cardId.contains("goal") -> "🎯"
        else -> "💡"
    }

    private fun getWhyItWorks(cardId: String): String = when {
        cardId.contains("50-30-20") -> StringKey.TIPS_WHY_50_30_20.tr()
        cardId.contains("automate") -> StringKey.TIPS_WHY_AUTOMATE.tr()
        cardId.contains("24-hour") || cardId.contains("wait") -> StringKey.TIPS_WHY_24_HOUR.tr()
        cardId.contains("compound") -> StringKey.TIPS_WHY_COMPOUND.tr()
        cardId.contains("small-wins") || cardId.contains("coffee") -> StringKey.TIPS_WHY_SMALL_WINS.tr()
        cardId.contains("emergency") -> StringKey.TIPS_WHY_EMERGENCY.tr()
        cardId.contains("tfsa") -> StringKey.TIPS_WHY_TFSA.tr()
        cardId.contains("rrsp") -> StringKey.TIPS_WHY_RRSP.tr()
        cardId.contains("fhsa") -> StringKey.TIPS_WHY_FHSA.tr()
        cardId.contains("account") || cardId.contains("roth") || cardId.contains("401k") -> StringKey.TIPS_WHY_ACCOUNTS.tr()
        else -> StringKey.TIPS_WHY_DEFAULT.tr()
    }

    private fun getExamplesForCard(cardId: String): List<Pair<String, String>> = when {
        cardId.contains("50-30-20") -> listOf(
            StringKey.TIPS_EXAMPLE_MONTHLY_INCOME.tr() to "$5,000",
            StringKey.TIPS_EXAMPLE_NEEDS.tr() to "$2,500",
            StringKey.TIPS_EXAMPLE_WANTS.tr() to "$1,500",
            StringKey.TIPS_EXAMPLE_SAVINGS.tr() to "$1,000"
        )
        cardId.contains("automate") -> listOf(
            StringKey.TIPS_EXAMPLE_PAYDAY.tr() to StringKey.TIPS_EXAMPLE_15TH_OF_MONTH.tr(),
            StringKey.TIPS_EXAMPLE_AUTO_TRANSFER.tr() to "$500",
            StringKey.TIPS_EXAMPLE_YEARLY_SAVINGS.tr() to "$6,000"
        )
        cardId.contains("compound") -> listOf(
            StringKey.TIPS_EXAMPLE_MONTHLY_CONTRIBUTION.tr() to "$200",
            StringKey.TIPS_EXAMPLE_TIME_PERIOD.tr() to StringKey.TIPS_EXAMPLE_20_YEARS.tr(),
            StringKey.TIPS_EXAMPLE_AT_RETURN.tr() to "~$120,000"
        )
        cardId.contains("small-wins") || cardId.contains("coffee") -> listOf(
            StringKey.TIPS_EXAMPLE_DAILY_SAVINGS.tr() to "$5",
            StringKey.TIPS_EXAMPLE_WEEKLY_TOTAL.tr() to "$35",
            StringKey.TIPS_EXAMPLE_YEARLY_TOTAL.tr() to "$1,825"
        )
        cardId.contains("emergency") -> listOf(
            StringKey.TIPS_EXAMPLE_MONTHLY_EXPENSES.tr() to "$3,000",
            StringKey.TIPS_EXAMPLE_3_MONTH_FUND.tr() to "$9,000",
            StringKey.TIPS_EXAMPLE_6_MONTH_FUND.tr() to "$18,000"
        )
        cardId.contains("tfsa") -> listOf(
            StringKey.TIPS_EXAMPLE_CONTRIBUTION_ROOM.tr() to "$7,000/year",
            StringKey.TIPS_EXAMPLE_WITHDRAWALS.tr() to StringKey.TIPS_EXAMPLE_TAX_FREE.tr(),
            StringKey.TIPS_EXAMPLE_BEST_FOR.tr() to StringKey.TIPS_EXAMPLE_FLEXIBLE_SAVINGS.tr()
        )
        cardId.contains("rrsp") -> listOf(
            StringKey.TIPS_EXAMPLE_CONTRIBUTION_LIMIT.tr() to "18% of income",
            StringKey.TIPS_EXAMPLE_TAX_BENEFIT.tr() to StringKey.TIPS_EXAMPLE_DEDUCTIBLE_NOW.tr(),
            StringKey.TIPS_EXAMPLE_BEST_FOR.tr() to StringKey.TIPS_EXAMPLE_RETIREMENT.tr()
        )
        cardId.contains("fhsa") -> listOf(
            StringKey.TIPS_EXAMPLE_ANNUAL_LIMIT.tr() to "$8,000",
            StringKey.TIPS_EXAMPLE_LIFETIME_MAX.tr() to "$40,000",
            StringKey.TIPS_EXAMPLE_BEST_FOR.tr() to StringKey.TIPS_EXAMPLE_FIRST_HOME.tr()
        )
        else -> emptyList()
    }

    private fun updateUI() {
        console.log("🔄 updateUI: containerElement=${if (containerElement != null) "exists" else "null"}")
        val container = containerElement ?: return

        // Save search input state before re-render
        val oldSearchInput = container.querySelector("#adviceSearchInput") as? org.w3c.dom.HTMLInputElement
        val wasSearchFocused = oldSearchInput == kotlinx.browser.document.activeElement
        val cursorPosition = oldSearchInput?.selectionStart ?: 0

        // Update goals list
        val goalsListContainer = container.querySelector("#goalsListContainer") as? HTMLElement
        if (goalsListContainer != null) {
            val goalsView = WebGoalsListView(
                viewModel = viewModel,
                rootContainer = container,
                onOpenStudio = { goal -> openStudioForGoal(goal) },
            )
            goalsListContainer.innerHTML = goalsView.render()
            goalsView.attachListeners()
        }

        // Update financial tips section
        val goalsContainer = container.querySelector("#goalsContainer") as? HTMLElement
        if (goalsContainer != null) {
            // Find or create financial tips container
            var financialTipsContainer = goalsContainer.querySelector("#financialTipsContainer") as? HTMLElement
            if (financialTipsContainer == null) {
                financialTipsContainer = document.createElement("div") as HTMLElement
                financialTipsContainer.id = "financialTipsContainer"
                goalsContainer.appendChild(financialTipsContainer)
            }
            financialTipsContainer.innerHTML = renderFinancialTipsSection()
            attachFinancialTipsListeners(container)
        }

        // Restore focus to search input if it was focused before
        if (wasSearchFocused) {
            val newSearchInput = container.querySelector("#adviceSearchInput") as? org.w3c.dom.HTMLInputElement
            newSearchInput?.focus()
            newSearchInput?.setSelectionRange(cursorPosition, cursorPosition)
        }
    }

    private fun showSubscriptionReviewModal() {
        val tm = WebTheme.current
        val backdrop = document.createElement("div") as HTMLElement
        backdrop.style.cssText = """
            position: fixed; inset: 0; background: ${if (tm.isDark) "rgba(0,0,0,0.72)" else "rgba(0,0,0,0.3)"};
            z-index: 1000; display: flex; align-items: flex-start; justify-content: center;
            overflow-y: auto; padding: 24px 16px; box-sizing: border-box;
        """.trimIndent()

        val isMobile = ResponsiveUtils.isMobile()
        val panel = document.createElement("div") as HTMLElement
        panel.style.cssText = """
            background: ${tm.card}; border-radius: 16px; width: 100%;
            max-width: ${if (isMobile) "100%" else "600px"}; max-height: 80vh;
            overflow-y: auto; padding: 24px; color: ${tm.textPrimary};
            box-shadow: ${if (tm.isDark) "none" else "0 20px 60px rgba(0,0,0,0.15)"};
        """.trimIndent()

        val titleText = i18n.Strings.get(i18n.StringKey.SUBSCRIPTION_CHECK_TITLE)
        panel.innerHTML = """
            <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:16px;">
                <h3 style="margin:0; font-size:18px; color:${tm.textPrimary};">$titleText</h3>
                <button id="closeSubModal" style="background:none; border:none; color:${tm.textSecondary}; font-size:24px; cursor:pointer; padding:4px 8px;">&times;</button>
            </div>
            <div style="font-size:13px; color:${tm.textSecondary}; margin-bottom:16px;">${i18n.Strings.get(i18n.StringKey.SUBSCRIPTION_CHECK_SUBTITLE)}</div>
            <div id="subscriptionsList" style="margin-top:8px;">
                <div style="text-align:center; padding:32px; color:${tm.textSecondary};">Loading...</div>
            </div>
            <div id="subscriptionTotal" style="margin-top:16px; padding-top:12px; border-top:1px solid ${tm.border}; display:none;"></div>
            <div id="subscriptionConfirmBtn" style="margin-top:16px; display:none;">
                <button id="confirmSubSelection" style="
                    width:100%; padding:14px; background:#4CAF50; color:white; border:none;
                    border-radius:10px; font-size:15px; font-weight:600; cursor:pointer;
                    transition: background 0.2s;
                " onmouseover="this.style.background='#43A047'" onmouseout="this.style.background='#4CAF50'">
                    ${i18n.Strings.get(i18n.StringKey.SUBSCRIPTION_CONFIRM_SELECTION)}
                </button>
            </div>
        """.trimIndent()

        var closed = false
        fun closeModal() { if (closed) return; closed = true; backdrop.remove() }

        backdrop.addEventListener("click", { e -> if (e.target == backdrop) closeModal() })
        panel.addEventListener("click", { it.stopPropagation() })
        backdrop.appendChild(panel)
        document.body?.appendChild(backdrop)
        panel.querySelector("#closeSubModal")?.addEventListener("click", { closeModal() })

        // Repeat icon SVG
        val repeatSvg = """<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 12v-3a3 3 0 0 1 3 -3h13m-3 -3l3 3l-3 3" /><path d="M20 12v3a3 3 0 0 1 -3 3h-13m3 3l-3 -3l3 -3" /></svg>"""

        // Track selected merchants locally
        val selectedMerchants = mutableSetOf<String>()

        // Fetch detected subscriptions
        coroutineScope.launch {
            try {
                val apiService = ApiService()
                val result = apiService.detectSubscriptions()
                val listContainer = panel.querySelector("#subscriptionsList") as? HTMLElement ?: return@launch
                val totalContainer = panel.querySelector("#subscriptionTotal") as? HTMLElement
                val confirmBtnContainer = panel.querySelector("#subscriptionConfirmBtn") as? HTMLElement

                if (result.subscriptions.isEmpty()) {
                    listContainer.innerHTML = "<div style='text-align:center; padding:32px; color:#888;'>${i18n.Strings.get(i18n.StringKey.SUBSCRIPTION_NONE_DETECTED)}</div>"
                    return@launch
                }

                // Pre-select already confirmed subscriptions
                result.subscriptions.forEach { sub ->
                    if (sub.status == "confirmed") selectedMerchants.add(sub.merchantKey)
                }

                fun updateTotal() {
                    val selectedSubs = result.subscriptions.filter { it.merchantKey in selectedMerchants }
                    val total = selectedSubs.sumOf { it.typicalAmount }
                    if (totalContainer != null && selectedSubs.isNotEmpty()) {
                        totalContainer.style.display = "block"
                        val formattedTotal = total.asDynamic().toFixed(2) as String
                        totalContainer.innerHTML = """
                            <div style="display:flex; justify-content:space-between; align-items:center;">
                                <span style="font-size:14px; color:#aaa;">${i18n.Strings.get(i18n.StringKey.SUBSCRIPTION_MONTHLY_TOTAL)}</span>
                                <span style="font-size:16px; font-weight:600; color:#66bb6a;">${"$"}$formattedTotal/mo</span>
                            </div>
                        """.trimIndent()
                    } else if (totalContainer != null) {
                        totalContainer.style.display = "none"
                    }
                }

                fun renderList() {
                    val html = StringBuilder()
                    result.subscriptions.forEach { sub ->
                        val formatted = sub.typicalAmount.asDynamic().toFixed(2) as String
                        val intervalLabel = when {
                            sub.averageIntervalDays in 6..8 -> i18n.Strings.get(i18n.StringKey.SUBSCRIPTION_WEEKLY)
                            sub.averageIntervalDays in 13..16 -> i18n.Strings.get(i18n.StringKey.SUBSCRIPTION_BIWEEKLY)
                            else -> i18n.Strings.get(i18n.StringKey.SUBSCRIPTION_MONTHLY)
                        }
                        val isSelected = sub.merchantKey in selectedMerchants
                        val checkboxIcon = if (isSelected) {
                            """<span style="color:#4CAF50; font-size:18px; cursor:pointer; width:22px; height:22px; display:inline-flex; align-items:center; justify-content:center;" data-merchant="${sub.merchantKey}" data-action="toggle-sub">✓</span>"""
                        } else {
                            """<span style="color:#555; font-size:18px; cursor:pointer; border:1.5px solid #555; border-radius:4px; width:22px; height:22px; display:inline-flex; align-items:center; justify-content:center;" data-merchant="${sub.merchantKey}" data-action="toggle-sub">&nbsp;</span>"""
                        }
                        val nameColor = if (isSelected) "white" else "#888"
                        val amountColor = if (isSelected) "#ef5350" else "#666"
                        val repeatIcon = if (isSelected) """<span style="color:#4CAF50; margin-right:8px;">$repeatSvg</span>""" else ""

                        html.append("""
                            <div style="display:flex; justify-content:space-between; align-items:center; padding:12px 0; border-bottom:1px solid rgba(255,255,255,0.06); cursor:pointer;" data-sub-row="${sub.merchantKey}" data-action="toggle-sub" data-merchant="${sub.merchantKey}">
                                <div style="display:flex; align-items:center; gap:12px;">
                                    $checkboxIcon
                                    <div>
                                        <div style="display:flex; align-items:center;">
                                            $repeatIcon
                                            <span style="font-size:14px; font-weight:500; color:$nameColor;">${sub.displayName}</span>
                                        </div>
                                        <div style="font-size:11px; color:#888; margin-top:2px;">${sub.occurrences}x &middot; $intervalLabel</div>
                                    </div>
                                </div>
                                <div style="font-size:14px; font-weight:600; color:$amountColor;">-${"$"}$formatted</div>
                            </div>
                        """)
                    }
                    listContainer.innerHTML = html.toString()
                    updateTotal()
                }

                renderList()
                confirmBtnContainer?.style?.display = "block"

                // Click handler: toggle checkbox selection
                listContainer.addEventListener("click", { evt ->
                    val target = evt.target as? HTMLElement ?: return@addEventListener
                    val actionEl = if (target.getAttribute("data-action") == "toggle-sub") target
                        else target.closest("[data-action=toggle-sub]") as? HTMLElement
                    val merchantKey = actionEl?.getAttribute("data-merchant") ?: return@addEventListener

                    if (merchantKey in selectedMerchants) {
                        selectedMerchants.remove(merchantKey)
                    } else {
                        selectedMerchants.add(merchantKey)
                    }
                    renderList()
                })

                // Confirm Selection button: confirm selected, dismiss unselected, close modal
                panel.querySelector("#confirmSubSelection")?.addEventListener("click", {
                    coroutineScope.launch {
                        try {
                            result.subscriptions.forEach { sub ->
                                val newStatus = if (sub.merchantKey in selectedMerchants) "confirmed" else "dismissed"
                                if (sub.status != newStatus) {
                                    apiService.updateSubscriptionStatus(sub.merchantKey, newStatus)
                                }
                            }
                            // Refresh the advice cards to update the subscription amount
                            personalizedAdviceComponent?.refresh()
                        } catch (e: Exception) {
                            console.error("Failed to save subscription selections: ${e.message}")
                        }
                    }
                    closeModal()
                })

            } catch (e: Exception) {
                val listContainer = panel.querySelector("#subscriptionsList") as? HTMLElement
                listContainer?.innerHTML = "<div style='text-align:center; padding:32px; color:#ef5350;'>Failed to load subscriptions</div>"
            }
        }
    }

    /**
     * Open the Goal Allocation Studio scoped to [goal]. Fetches the user's
     * profile first — without one, the math engine has no tax context, so we
     * surface a small "set up your profile first" placeholder and let the
     * onboarding stepper take over on next Create Goal.
     */
    private fun openStudioForGoal(goal: SavingsGoal) {
        // Debt-payoff goals get the sibling Debt Paydown Studio instead of
        // the account-allocation Studio. They share the modal pattern but
        // model totally different math (amortization vs portfolio growth).
        // Both goal kinds now open a simple edit form; the heavy explorers are
        // the standalone Goal Lab (savings) / Debt Studio (debt), reached from
        // the form's CTA or the Goals-page Lab button.
        val container = containerElement ?: document.body ?: return
        if (goal.category == models.GoalCategory.DEBT_PAYOFF) {
            WebGoalDialogHelper.showDebtGoalFormDialog(container, viewModel, goal)
        } else {
            WebGoalDialogHelper.showGoalFormDialog(container, viewModel, goal)
        }
    }

    /**
     * Gate a goal-related action behind the Studio onboarding stepper.
     *
     * Per spec §14.1 the stepper triggers on first Studio open. The Studio
     * screen itself ships in PR 3; in the interim we hook into Create Goal
     * (the most natural goal-related entry point on web) so the user lands
     * on the stepper at a meaningful moment rather than via a banner that
     * appears out of nowhere.
     *
     * If a profile already exists or the GET fails (server unavailable
     * shouldn't block goal creation), [action] runs immediately. Otherwise
     * the stepper opens first and [action] runs after the user finishes or
     * skips out.
     */
    private fun gateWithStudioProfile(action: () -> Unit) {
        val apiService = ApiService()
        GlobalScope.launch {
            val existing: studio.Wiz3rUserProfile? = try {
                apiService.getStudioProfile()
            } catch (_: Exception) {
                null  // server down or auth issue — don't block goal creation
            }
            if (existing != null) {
                action()
                return@launch
            }
            WebStudioOnboardingComponent(
                apiService = apiService,
                initial = null,
                onComplete = action,
            ).show()
        }
    }

    private fun showFilteredTransactionsModal(category: String?, merchants: List<String>, title: String) {
        val tm = WebTheme.current
        val backdrop = document.createElement("div") as HTMLElement
        backdrop.style.cssText = """
            position: fixed; inset: 0; background: ${if (tm.isDark) "rgba(0,0,0,0.72)" else "rgba(0,0,0,0.3)"};
            z-index: 1000; display: flex; align-items: flex-start; justify-content: center;
            overflow-y: auto; padding: 24px 16px; box-sizing: border-box;
        """.trimIndent()

        val isMobile = ResponsiveUtils.isMobile()
        val panel = document.createElement("div") as HTMLElement
        panel.style.cssText = """
            background: ${tm.card}; border-radius: 16px; width: 100%;
            max-width: ${if (isMobile) "100%" else "600px"}; max-height: 80vh;
            overflow-y: auto; padding: 24px; color: ${tm.textPrimary};
            box-shadow: ${if (tm.isDark) "none" else "0 20px 60px rgba(0,0,0,0.15)"};
        """.trimIndent()

        val filterLabel = if (category != null) {
            val displayCat = category.replace("_", " ").lowercase().replaceFirstChar { it.uppercase() }
            "<span style='background:rgba(124,77,255,0.15); border:1px solid rgba(124,77,255,0.4); border-radius:16px; padding:4px 12px; font-size:12px; color:${if (tm.isDark) "#b39ddb" else "#6A1B9A"};'>$displayCat</span>"
        } else ""

        panel.innerHTML = """
            <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:16px;">
                <h3 style="margin:0; font-size:18px; color:${tm.textPrimary};">$title</h3>
                <button id="closeFilteredModal" style="background:none; border:none; color:${tm.textSecondary}; font-size:24px; cursor:pointer; padding:4px 8px;">&times;</button>
            </div>
            $filterLabel
            <div id="filteredTransactionsList" style="margin-top:16px;">
                <div style="text-align:center; padding:32px; color:${tm.textSecondary};">Loading transactions...</div>
            </div>
        """.trimIndent()

        var closed = false
        fun closeModal() {
            if (closed) return
            closed = true
            backdrop.remove()
        }

        backdrop.addEventListener("click", { e -> if (e.target == backdrop) closeModal() })
        panel.addEventListener("click", { it.stopPropagation() })
        backdrop.appendChild(panel)
        document.body?.appendChild(backdrop)

        panel.querySelector("#closeFilteredModal")?.addEventListener("click", { closeModal() })
        kotlinx.browser.document.addEventListener("keydown", { e ->
            if (!closed && e.asDynamic().key == "Escape") closeModal()
        })

        // Fetch transactions and render filtered list
        coroutineScope.launch {
            try {
                val apiService = ApiService()
                val response = apiService.getTransactionsFromServer()
                val listContainer = panel.querySelector("#filteredTransactionsList") as? HTMLElement ?: return@launch

                val filtered = response.transactions.filter { tx ->
                    val amountIsDebit = tx.amount > 0
                    if (!amountIsDebit && category != "INCOME") return@filter false

                    // When merchants are provided, filter by merchant name (not category)
                    if (merchants.isNotEmpty()) {
                        val name = (tx.name ?: tx.merchant_name ?: "").lowercase()
                        return@filter merchants.any { name.contains(it.lowercase()) }
                    }

                    // Otherwise filter by category
                    if (category != null) {
                        return@filter tx.personal_finance_category?.primary?.equals(category, ignoreCase = true) == true
                    }

                    true
                }

                if (filtered.isEmpty()) {
                    listContainer.innerHTML = "<div style='text-align:center; padding:32px; color:${tm.textSecondary};'>No matching transactions found</div>"
                    return@launch
                }

                val html = StringBuilder()
                filtered.sortedByDescending { it.date }.take(50).forEach { tx ->
                    val name = tx.name ?: tx.merchant_name ?: "Unknown"
                    val amount = tx.amount
                    val absAmount = if (amount > 0) amount else -amount
                    val formatted = absAmount.asDynamic().toFixed(2) as String
                    val amountStr = if (amount > 0) "-\$$formatted" else "+\$$formatted"
                    val amountColor = if (amount > 0) tm.expenseRed else tm.incomeGreen
                    val cat = tx.personal_finance_category?.let {
                        utils.CategoryUtils.formatTwoLevelCategoryLocalized(it.primary, it.detailed)
                    } ?: ""

                    html.append("""
                        <div style="display:flex; justify-content:space-between; align-items:center; padding:12px 0; border-bottom:1px solid ${if (tm.isDark) "rgba(255,255,255,0.06)" else tm.border};">
                            <div>
                                <div style="font-size:14px; font-weight:500; color:${tm.textPrimary};">$name</div>
                                <div style="font-size:11px; color:${tm.textSecondary}; margin-top:2px;">${tx.date} &middot; $cat</div>
                            </div>
                            <div style="font-size:14px; font-weight:600; color:$amountColor;">$amountStr</div>
                        </div>
                    """)
                }

                listContainer.innerHTML = html.toString()
            } catch (e: Exception) {
                val listContainer = panel.querySelector("#filteredTransactionsList") as? HTMLElement
                listContainer?.innerHTML = "<div style='text-align:center; padding:32px; color:#ef5350;'>Failed to load transactions</div>"
            }
        }
    }
}

/**
 * Reusable view for displaying and managing the list of savings goals.
 * Can be embedded in other components (like Dashboard).
 */
class WebGoalsListView(
    private val viewModel: WebSavingsGoalsViewModel,
    private val rootContainer: HTMLElement, // The container where listeners should be attached (usually the parent of the list)
    private val onOpenStudio: ((SavingsGoal) -> Unit)? = null, // Card click routes the goal into the Studio (single edit surface).
) {

    fun render(): String {
        return if (viewModel.isLoading) {
            renderLoadingState()
        } else if (viewModel.goals.isEmpty()) {
            renderEmptyState()
        } else {
            renderGoalsList()
        }
    }

    private fun renderEmptyState(): String =
        """
        <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 4rem 2rem; text-align: center;">
            <div style="font-size: 6rem; margin-bottom: 1.5rem; opacity: 0.3;">🎯</div>
            <h2 style="color: #ffffff; font-size: 1.75rem; margin-bottom: 1rem; font-weight: 600;">${StringKey.GOALS_NO_GOALS_TITLE.tr()}</h2>
            <p style="color: #888; font-size: 1rem; margin-bottom: 2rem; max-width: 400px;">
                ${StringKey.GOALS_NO_GOALS_DESC.tr()}
            </p>
            <button id="emptyStateAddButton" style="background: #00d9ff; color: #0a0a0a; border: none; padding: 1rem 2rem; border-radius: 12px; font-size: 1rem; font-weight: 600; cursor: pointer; transition: all 0.2s;"
                onmouseover="this.style.background='#00c5e5'"
                onmouseout="this.style.background='#00d9ff'">
                + ${StringKey.GOALS_CREATE_FIRST.tr()}
            </button>
        </div>
        """.trimIndent()

    private fun renderLoadingState(): String =
        """
        <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 4rem 2rem; text-align: center;">
            <div style="color: #888; font-size: 1.5rem;">${StringKey.GOALS_LOADING.tr()}</div>
        </div>
        """.trimIndent()

    private fun renderGoalsList(): String =
        viewModel.goals.joinToString("") { goal ->
            // Format date for display (e.g. "Nov 27, 2026")
            val months = listOf(
                StringKey.MONTH_JAN.tr(), StringKey.MONTH_FEB.tr(), StringKey.MONTH_MAR.tr(),
                StringKey.MONTH_APR.tr(), StringKey.MONTH_MAY.tr(), StringKey.MONTH_JUN.tr(),
                StringKey.MONTH_JUL.tr(), StringKey.MONTH_AUG.tr(), StringKey.MONTH_SEP.tr(),
                StringKey.MONTH_OCT.tr(), StringKey.MONTH_NOV.tr(), StringKey.MONTH_DEC.tr()
            )
            val displayDate = "${months[goal.deadline.monthNumber - 1]} ${goal.deadline.dayOfMonth}, ${goal.deadline.year}"

            // Determine priority color and label
            val (priorityColor, priorityLabel) = when (goal.priority) {
                GoalPriority.MUST_HAVE -> "#ff6b6b" to StringKey.GOALS_PRIORITY_MUST_HAVE.tr()
                GoalPriority.NICE_TO_HAVE -> "#ff9f43" to StringKey.GOALS_PRIORITY_NICE_TO_HAVE.tr()
                GoalPriority.STRETCH_GOAL -> "#48dbfb" to StringKey.GOALS_PRIORITY_STRETCH_GOAL.tr()
                GoalPriority.MAYBE -> "#c8d6e5" to StringKey.GOALS_PRIORITY_MAYBE.tr()
                else -> "#ff9f43" to StringKey.GOALS_PRIORITY_NICE_TO_HAVE.tr()
            }

            // Monthly contribution and the pace badge both come from the shared
            // computeGoalPace. This used to count calendar boundaries itself —
            // (deadlineYear - currentYear) * 12 + (deadlineMonth - currentMonth) — which
            // counts a part-month as whole and so divided by 67 where the shared rule
            // divides by 66: the same goal read $411/mo here and $417/mo on iOS.
            val pace = analytics.computeGoalPace(goal)
            val monthsRemaining = analytics.goalMonthsRemaining(goal)
            val monthlyContribution = pace.requiredMonthly

            // Calculate progress percentage
            // Bar width, so it clamps; the shared definition owns the arithmetic.
            val progressPercent = analytics.goalProgressBarPercent(goal)

            // Single source of truth for goal-icon rendering: icon ID → SVG
            // outline, legacy emoji → resolved outline (or the emoji if truly
            // unknown). See CategoryIconSvg.renderGoalIcon.
            val goalIconSvg = CategoryIconSvg.renderGoalIcon(goal.iconEmoji, size = 28, color = "#ffffff")

            // Honest pace status (shared): "Behind" when saved < 85% of the
            // steady plan by now — replaces the old always-"On Track" badge.
            val (statusColor, statusLabel, statusIcon) = when (pace.status) {
                analytics.GoalPaceStatus.BEHIND -> Triple("#ff9f43", StringKey.GOALS_BEHIND.tr(), "!")
                analytics.GoalPaceStatus.AHEAD -> Triple("#4CAF50", StringKey.GOALS_AHEAD.tr(), "↑")
                else -> Triple(WebTheme.current.accent, StringKey.GOALS_ON_TRACK.tr(), "✓")
            }

            """
            <div class="goalCard" id="goal-${goal.id}" data-goal-id="${goal.id}" style="background: ${WebTheme.current.card}; border-radius: 12px; padding: 1.25rem; border: 1px solid ${WebTheme.current.border}; box-shadow: ${WebTheme.current.cardShadow}; cursor: pointer; transition: all 0.2s;">

                <!-- Top Row: Icon + Name + Date -->
                <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.25rem;">
                    <div style="display: flex; align-items: center; gap: 0.75rem;">
                        $goalIconSvg
                        <div style="font-size: 1.1rem; font-weight: 600; color: ${WebTheme.current.textPrimary};">${goal.name}</div>
                    </div>
                    <div style="font-size: 0.9rem; color: ${WebTheme.current.textSecondary};">
                        ${StringKey.GOALS_TARGET_DATE.tr(displayDate)}
                    </div>
                </div>

                <!-- Priority Label -->
                <div style="font-size: 0.7rem; color: ${WebTheme.current.textSecondary}; font-weight: 500; letter-spacing: 0.5px; margin-bottom: 0.75rem; margin-left: 1.25rem;">
                    $priorityLabel
                </div>

                <!-- Amount Row -->
                <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem; margin-left: 1.25rem;">
                    <div style="font-size: 1.25rem; color: ${WebTheme.current.textPrimary};">
                        $${goal.currentAmount.toInt()} / $${goal.targetAmount.toInt()}
                    </div>
                    <div style="font-size: 1.1rem; font-weight: 700; color: ${WebTheme.current.accent};">
                        $${monthlyContribution.toInt()}${StringKey.GOALS_PER_MONTH.tr()}
                    </div>
                </div>

                <!-- Progress Bar -->
                <div style="width: 100%; height: 6px; background: ${WebTheme.current.border}; border-radius: 3px; margin-bottom: 0.75rem; margin-left: 1.25rem; width: calc(100% - 1.25rem); overflow: hidden;">
                    <div style="width: ${progressPercent}%; height: 100%; background: $priorityColor; border-radius: 3px;"></div>
                </div>

                <!-- Footer Status -->
                <div style="border-top: 1px solid ${WebTheme.current.border}; padding-top: 0.75rem; display: flex; align-items: center; gap: 0.5rem; margin-left: 1.25rem;">
                    <span style="color: $statusColor; font-size: 0.9rem;">$statusIcon</span>
                    <span style="color: $statusColor; font-size: 0.9rem; font-weight: 500;">$statusLabel</span>
                </div>
            </div>
            """.trimIndent()
        }

    fun attachListeners() {
        // Goal card click → open the Goal Allocation Studio directly. The
        // Studio is the single edit surface for a goal (name, target,
        // deadline, category, account-type pinning, sliders, Save/Cancel/
        // Delete); no inline edit form is rendered on the card.
        val goalCards = rootContainer.querySelectorAll(".goalCard")
        for (i in 0 until goalCards.length) {
            val card = goalCards.item(i) as? HTMLElement
            card?.addEventListener("click", { event ->
                val goalId = (event.currentTarget as? HTMLElement)?.getAttribute("data-goal-id")
                goalId?.let { id ->
                    val goal = viewModel.goals.firstOrNull { it.id == id } ?: return@let
                    onOpenStudio?.invoke(goal)
                }
            })
        }

        // Empty state "+ Create your first goal" button — opens the type
        // picker. Each Studio handles its own create flow inline; the
        // onOpenStudio dispatcher (which routes to the right Studio for
        // existing goals) isn't needed here.
        val emptyButton = rootContainer.querySelector("#emptyStateAddButton") as? HTMLElement
        emptyButton?.addEventListener("click", {
            val dialogContainer = rootContainer.closest(".dashboard-container") as? HTMLElement ?: rootContainer
            WebGoalDialogHelper.showGoalTypePicker(dialogContainer, viewModel)
        })
    }
}

/**
 * Helper for goal-create entry points.
 *
 * The big "Add new goal" form was retired in favor of a two-button type
 * picker — Savings goal opens the Allocation Studio in create mode, Debt
 * paydown opens the Debt Studio in create mode. Each Studio handles all
 * the goal entry (name, target, deadline, category, account-type for
 * savings; name, balance, rate, term for debt) inline and commits via
 * viewModel.createGoal on Save.
 *
 * One legacy entry point remains: TransactionDetailComponent's "Create a
 * goal" link, which has only a repository (no ViewModel) at hand. The
 * [showAddGoalDialog] repository overload at the bottom of this object
 * is retained for that path — TODO: refactor to use the picker once we
 * thread a ViewModel through the transaction detail tree.
 */
object WebGoalDialogHelper {

    /** Pops the small two-button type picker. Replaces the old big create
     *  dialog. Each button opens the matching Studio in create mode with
     *  a stub draft goal. */
    fun showGoalTypePicker(
        container: HTMLElement,
        viewModel: WebSavingsGoalsViewModel,
        onCompleted: (() -> Unit)? = null,
    ) {
        val tm = WebTheme.current
        var dialog = container.querySelector("#goalTypePicker") as? HTMLElement
        if (dialog == null) {
            dialog = document.createElement("div") as HTMLElement
            dialog.id = "goalTypePicker"
            dialog.style.cssText = "display: none; position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: ${if (tm.isDark) "rgba(0,0,0,0.8)" else "rgba(0,0,0,0.3)"}; z-index: 1000; align-items: center; justify-content: center;"
            container.appendChild(dialog)
        }

        val btnBg = if (tm.isDark) "#00d9ff" else tm.accentStrong
        val btnFg = if (tm.isDark) "#0a0a0a" else "#ffffff"

        dialog.innerHTML = """
            <div style="background: ${tm.card}; border-radius: 16px; padding: 2rem; max-width: 420px; width: 90%; border: 1px solid ${tm.border}; box-shadow: ${if (tm.isDark) "none" else "0 20px 60px rgba(0,0,0,0.15)"};">
                <h2 style="margin: 0 0 1.5rem 0; color: ${tm.textPrimary}; font-size: 1.25rem; font-weight: 600; text-align: center;">${StringKey.GOAL_TYPE_PICKER_TITLE.tr()}</h2>
                <div style="display: flex; flex-direction: column; gap: 12px;">
                    <button id="goalTypeSavings" style="background: $btnBg; color: $btnFg; border: none; padding: 14px 18px; border-radius: 10px; font-size: 1rem; font-weight: 600; cursor: pointer; transition: all 0.2s;">${StringKey.GOAL_TYPE_PICKER_SAVINGS.tr()}</button>
                    <button id="goalTypeDebt" style="background: $btnBg; color: $btnFg; border: none; padding: 14px 18px; border-radius: 10px; font-size: 1rem; font-weight: 600; cursor: pointer; transition: all 0.2s;">${StringKey.GOAL_TYPE_PICKER_DEBT.tr()}</button>
                </div>
                <div style="display: flex; justify-content: center; margin-top: 1.25rem;">
                    <button id="goalTypeCancel" style="background: ${tm.background}; color: ${tm.textSecondary}; border: 1px solid ${tm.border}; padding: 8px 16px; border-radius: 8px; cursor: pointer; font-size: 0.85rem;">${StringKey.ACTION_CANCEL.tr()}</button>
                </div>
            </div>
        """.trimIndent()
        dialog.style.display = "flex"

        val close = { dialog.style.display = "none" }
        dialog.querySelector("#goalTypeCancel")?.addEventListener("click", { close() })
        dialog.addEventListener("click", { event -> if (event.target == dialog) close() })

        dialog.querySelector("#goalTypeSavings")?.addEventListener("click", {
            close()
            showGoalFormDialog(container, viewModel)
        })
        dialog.querySelector("#goalTypeDebt")?.addEventListener("click", {
            close()
            showDebtGoalFormDialog(container, viewModel)
        })
    }

    // -----------------------------------------------------------------------
    // Simple goal create/edit form (revived, decoupled from the Studio).
    // The heavy scenario explorer now lives standalone as the "Goal Lab",
    // reachable from a button on the Goals page and the CTA below.
    // -----------------------------------------------------------------------

    private fun prettyAccountLabel(t: models.GoalAccountType): String = when (t) {
        models.GoalAccountType.HISA -> StringKey.GOAL_ACCOUNT_LABEL_HISA.tr()
        models.GoalAccountType.TFSA -> StringKey.GOAL_ACCOUNT_LABEL_TFSA.tr()
        models.GoalAccountType.RRSP -> StringKey.GOAL_ACCOUNT_LABEL_RRSP.tr()
        models.GoalAccountType.FHSA -> StringKey.GOAL_ACCOUNT_LABEL_FHSA.tr()
        models.GoalAccountType.RESP -> StringKey.GOAL_ACCOUNT_LABEL_RESP.tr()
        models.GoalAccountType.RDSP -> StringKey.GOAL_ACCOUNT_LABEL_RDSP.tr()
        models.GoalAccountType.NON_REGISTERED -> StringKey.GOAL_ACCOUNT_LABEL_NON_REGISTERED.tr()
        models.GoalAccountType.EMPLOYER_PENSION -> StringKey.GOAL_ACCOUNT_LABEL_EMPLOYER_PENSION.tr()
        models.GoalAccountType.CHEQUING -> StringKey.GOAL_ACCOUNT_LABEL_CHEQUING.tr()
        models.GoalAccountType.OTHER -> StringKey.GOAL_ACCOUNT_LABEL_OTHER.tr()
    }

    /** Account-type dropdown for the simple form. Empty string when the
     *  category has no account options (e.g. DEBT_PAYOFF). */
    private fun renderAccountFieldsForDialog(
        category: models.GoalCategory,
        selected: models.GoalAccountType?,
    ): String {
        val t = WebTheme.current
        val options = models.GoalAccountType.optionsFor(category)
        if (options.isEmpty()) return ""
        val currentValue = selected?.name ?: ""
        val optionTags = buildString {
            append("""<option value=""${if (currentValue.isBlank()) " selected" else ""}>${StringKey.GOAL_ACCOUNT_NOT_SET.tr()}</option>""")
            for (opt in options) {
                val sel = if (opt.name == currentValue) " selected" else ""
                append("""<option value="${opt.name}"$sel>${prettyAccountLabel(opt)}</option>""")
            }
        }
        return """
            <label style="display: block; margin-bottom: 0.5rem; color: ${t.textSecondary}; font-size: 0.875rem; font-weight: 500;">${StringKey.STUDIO_ACCOUNT_TYPE_SECTION_TITLE.tr()}</label>
            <select id="dialogAccountTypeSelect"
                style="width:100%; padding: 10px 28px 10px 12px; font-size: 0.9rem; color: ${t.textPrimary}; background: ${t.background}; border: 1px solid ${t.border}; border-radius: 8px; outline: none; font-family: inherit; appearance: none; -webkit-appearance: none;">
                $optionTags
            </select>
        """.trimIndent()
    }

    private data class GoalFormState(
        val name: String,
        val amount: Double,
        val deadline: Date,
        val category: models.GoalCategory,
        val priority: models.GoalPriority,
        val icon: String,
        val accountType: models.GoalAccountType?,
    )

    private fun collectGoalForm(dialog: HTMLElement, defaultIconId: String, requireValid: Boolean): GoalFormState? {
        val nameInput = dialog.querySelector("#goalNameInput") as? org.w3c.dom.HTMLInputElement
        val amountInput = dialog.querySelector("#goalAmountInput") as? org.w3c.dom.HTMLInputElement
        val dateInput = dialog.querySelector("#goalDateInput") as? org.w3c.dom.HTMLInputElement
        val categorySelect = dialog.querySelector("#goalCategorySelect") as? org.w3c.dom.HTMLInputElement
        val prioritySelect = dialog.querySelector("#goalPrioritySelect") as? org.w3c.dom.HTMLSelectElement
        val iconInput = dialog.querySelector("#goalIconInput") as? org.w3c.dom.HTMLInputElement
        val accountSelect = dialog.querySelector("#dialogAccountTypeSelect") as? org.w3c.dom.HTMLSelectElement

        val name = nameInput?.value?.trim() ?: ""
        val amount = amountInput?.value?.toDoubleOrNull() ?: 0.0
        val dateStr = dateInput?.value ?: ""
        val categoryStr = categorySelect?.value ?: "EMERGENCY_FUND"
        val priorityStr = prioritySelect?.value ?: "NICE_TO_HAVE"
        val icon = iconInput?.value ?: defaultIconId

        if (requireValid && name.isEmpty()) { nameInput?.style?.borderColor = "#ff4757"; return null }
        if (requireValid && amount <= 0) { amountInput?.style?.borderColor = "#ff4757"; return null }

        val category = runCatching { models.GoalCategory.valueOf(categoryStr) }.getOrDefault(models.GoalCategory.OTHER)
        val priority = runCatching { models.GoalPriority.valueOf(priorityStr) }.getOrDefault(models.GoalPriority.NICE_TO_HAVE)
        val deadline = if (dateStr.isNotEmpty()) Date(dateStr) else Date()
        val accountType = accountSelect?.value?.takeIf { it.isNotBlank() }?.let { runCatching { models.GoalAccountType.valueOf(it) }.getOrNull() }
        return GoalFormState(name.ifEmpty { "" }, if (amount > 0) amount else 10_000.0, deadline, category, priority, icon, accountType)
    }

    /** The simple create/edit form. `existing == null` → create; otherwise edit. */
    fun showGoalFormDialog(
        container: HTMLElement,
        viewModel: WebSavingsGoalsViewModel,
        existing: models.SavingsGoal? = null,
        initialCategory: models.GoalCategory = models.GoalCategory.EMERGENCY_FUND,
    ) {
        val tm = WebTheme.current
        val isEdit = existing != null

        var dialog = container.querySelector("#goalFormDialog") as? HTMLElement
        if (dialog == null) {
            dialog = document.createElement("div") as HTMLElement
            dialog.id = "goalFormDialog"
            dialog.style.cssText = "display: none; position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: ${if (tm.isDark) "rgba(0,0,0,0.8)" else "rgba(0,0,0,0.3)"}; z-index: 1000; align-items: center; justify-content: center;"
            container.appendChild(dialog)
        }

        val category0 = existing?.category ?: initialCategory
        val account0 = existing?.accountType
        val icon0 = existing?.iconEmoji?.takeIf { it.isNotBlank() } ?: "tax"
        val name0 = existing?.name ?: ""
        val amount0 = existing?.targetAmount

        val now = Date()
        val defaultDateStr = "${now.getFullYear() + 1}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}"
        val date0 = existing?.deadline?.let { "${it.year}-${it.monthNumber.toString().padStart(2, '0')}-${it.dayOfMonth.toString().padStart(2, '0')}" } ?: defaultDateStr

        val categoryOptions = listOf(
            "EMERGENCY_FUND" to StringKey.GOAL_CAT_EMERGENCY_FUND.tr(),
            "VACATION" to StringKey.GOAL_CAT_VACATION.tr(),
            "HOME_PURCHASE" to StringKey.GOAL_CAT_HOME_PURCHASE.tr(),
            "RETIREMENT" to StringKey.GOAL_CAT_RETIREMENT.tr(),
            "EDUCATION" to StringKey.GOAL_CAT_EDUCATION.tr(),
            "MAJOR_PURCHASE" to StringKey.GOAL_CAT_MAJOR_PURCHASE.tr(),
            "OTHER" to StringKey.GOAL_CAT_OTHER.tr(),
        )
        val priorityOptions = listOf(
            "MUST_HAVE" to StringKey.GOAL_PRI_MUST_HAVE.tr(),
            "NICE_TO_HAVE" to StringKey.GOAL_PRI_NICE_TO_HAVE.tr(),
            "STRETCH_GOAL" to StringKey.GOAL_PRI_STRETCH_GOAL.tr(),
            "MAYBE" to StringKey.GOAL_PRI_MAYBE.tr(),
        )

        val iconIds = utils.CategoryIconPaths.goalIconOptions
        val cat0Value = category0.name
        val cat0IconId = utils.CategoryIconPaths.goalCategoryIconId[cat0Value] ?: "target_arrow"
        val cat0Svg = utils.CategoryIconPaths.getSvgContent(cat0IconId)
        val cat0Label = categoryOptions.firstOrNull { it.first == cat0Value }?.second ?: categoryOptions.first().second

        val categoryOptionsHtml = categoryOptions.joinToString("") { (value, label) ->
            val iconId = utils.CategoryIconPaths.goalCategoryIconId[value] ?: "target_arrow"
            val svgPaths = utils.CategoryIconPaths.getSvgContent(iconId)
            """<div class="goal-cat-option" data-value="$value" style="display: flex; align-items: center; gap: 10px; padding: 10px 14px; cursor: pointer; border-radius: 8px; transition: background 0.15s;">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="${tm.textPrimary}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">$svgPaths</svg>
                <span style="color: ${tm.textPrimary}; font-size: 14px;">$label</span>
            </div>"""
        }
        val priorityOptionsHtml = priorityOptions.joinToString("") { (value, label) ->
            val sel = if (value == existing?.priority?.name || (existing == null && value == "NICE_TO_HAVE")) "selected" else ""
            """<option value="$value" $sel>$label</option>"""
        }
        val iconOptionsHtml = iconIds.joinToString("") { iconId ->
            val svgPaths = utils.CategoryIconPaths.getSvgContent(iconId)
            val on = iconId == icon0
            """<button type="button" class="icon-option" data-icon="$iconId"
                style="width: 40px; height: 40px; border: 2px solid ${if (on) tm.accent else tm.border}; border-radius: 8px; background: ${if (on) "${tm.accent}33" else tm.background}; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; justify-content: center;">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="${tm.textPrimary}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">$svgPaths</svg>
            </button>"""
        }

        val inputBg = tm.background
        val inputBorder = tm.border
        val addBtnBg = if (tm.isDark) "#00d9ff" else tm.accentStrong
        val addBtnFg = if (tm.isDark) "#0a0a0a" else "#ffffff"
        val title = if (isEdit) name0.ifBlank { StringKey.GOAL_DIALOG_TITLE.tr() } else StringKey.GOAL_DIALOG_TITLE.tr()
        val saveLabel = if (isEdit) StringKey.STUDIO_SAVE_ACTION.tr() else StringKey.GOAL_DIALOG_CREATE.tr()
        val deleteBtnHtml = if (isEdit) """<button id="goalDeleteButton" style="background: transparent; color: #ff4757; border: 1px solid #ff475766; padding: 0.75rem 1.25rem; border-radius: 8px; cursor: pointer; font-size: 0.9rem; font-weight: 500;">${StringKey.STUDIO_DELETE_GOAL.tr()}</button>""" else "<span></span>"

        dialog.innerHTML = """
            <style>.goal-cat-option:hover { background: ${if (tm.isDark) "#2a2a2a" else tm.background}; }</style>
            <div style="background: ${tm.card}; border-radius: 16px; padding: 2rem; max-width: 500px; width: 90%; max-height: 85vh; overflow-y: auto; border: 1px solid ${tm.border}; box-shadow: ${if (tm.isDark) "none" else "0 20px 60px rgba(0,0,0,0.15)"};">
                <h2 style="margin: 0 0 1.5rem 0; color: ${tm.textPrimary}; font-size: 1.5rem; font-weight: 600;">$title</h2>
                <div style="margin-bottom: 1.25rem;">
                    <label style="display: block; margin-bottom: 0.5rem; color: ${tm.textSecondary}; font-size: 0.875rem; font-weight: 500;">${StringKey.GOAL_DIALOG_NAME_LABEL.tr()}</label>
                    <input id="goalNameInput" type="text" value="$name0" placeholder="${StringKey.GOAL_DIALOG_NAME_PLACEHOLDER.tr()}"
                        style="width: 100%; padding: 0.75rem; border: 1px solid $inputBorder; border-radius: 8px; font-size: 1rem; box-sizing: border-box; background: $inputBg; color: ${tm.textPrimary};">
                </div>
                <div style="margin-bottom: 1.25rem;">
                    <label style="display: block; margin-bottom: 0.5rem; color: ${tm.textSecondary}; font-size: 0.875rem; font-weight: 500;">${StringKey.GOAL_DIALOG_AMOUNT_LABEL.tr()}</label>
                    <div style="position: relative;">
                        <span style="position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: ${tm.textSecondary}; font-size: 1rem;">$</span>
                        <input id="goalAmountInput" type="number" value="${amount0?.let { (if (it % 1.0 == 0.0) it.toInt().toString() else it.toString()) } ?: ""}" placeholder="10000" min="1" step="100"
                            style="width: 100%; padding: 0.75rem 0.75rem 0.75rem 28px; border: 1px solid $inputBorder; border-radius: 8px; font-size: 1rem; box-sizing: border-box; background: $inputBg; color: ${tm.textPrimary};">
                    </div>
                </div>
                <div style="margin-bottom: 1.25rem;">
                    <label style="display: block; margin-bottom: 0.5rem; color: ${tm.textSecondary}; font-size: 0.875rem; font-weight: 500;">${StringKey.GOAL_DIALOG_DATE_LABEL.tr()}</label>
                    <input id="goalDateInput" type="date" value="$date0"
                        style="width: 100%; padding: 0.75rem; border: 1px solid $inputBorder; border-radius: 8px; font-size: 1rem; box-sizing: border-box; background: $inputBg; color: ${tm.textPrimary};">
                </div>
                <div style="margin-bottom: 1.25rem;">
                    <label style="display: block; margin-bottom: 0.5rem; color: ${tm.textSecondary}; font-size: 0.875rem; font-weight: 500;">${StringKey.GOAL_DIALOG_CATEGORY_LABEL.tr()}</label>
                    <div id="goalCategoryDropdown" style="position: relative;">
                        <div id="goalCategoryTrigger" style="display: flex; align-items: center; gap: 10px; padding: 0.75rem; border: 1px solid $inputBorder; border-radius: 8px; background: $inputBg; cursor: pointer;">
                            <svg id="goalCatIcon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="${tm.textPrimary}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">$cat0Svg</svg>
                            <span id="goalCatLabel" style="color: ${tm.textPrimary}; flex: 1; font-size: 1rem;">$cat0Label</span>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${tm.textSecondary}" stroke-width="2"><path d="M6 9l6 6l6 -6"/></svg>
                        </div>
                        <div id="goalCategoryMenu" style="display: none; position: absolute; left: 0; right: 0; top: calc(100% + 4px); background: ${tm.card}; border: 1px solid $inputBorder; border-radius: 8px; z-index: 10; max-height: 250px; overflow-y: auto;">
                            $categoryOptionsHtml
                        </div>
                        <input type="hidden" id="goalCategorySelect" value="$cat0Value">
                    </div>
                </div>
                <div id="goalAccountWrapper" style="margin-bottom: 1.25rem;">${renderAccountFieldsForDialog(category0, account0)}</div>
                <div style="margin-bottom: 1.25rem;">
                    <label style="display: block; margin-bottom: 0.5rem; color: ${tm.textSecondary}; font-size: 0.875rem; font-weight: 500;">${StringKey.GOAL_DIALOG_PRIORITY_LABEL.tr()}</label>
                    <select id="goalPrioritySelect" style="width: 100%; padding: 0.75rem; border: 1px solid $inputBorder; border-radius: 8px; font-size: 1rem; box-sizing: border-box; background: $inputBg; color: ${tm.textPrimary}; cursor: pointer;">
                        $priorityOptionsHtml
                    </select>
                </div>
                <div id="goalFormLabCta" style="margin-bottom: 1.25rem; padding: 0.9rem 1rem; border: 1px dashed ${tm.accent}; border-radius: 10px; background: ${tm.accent}12; cursor: pointer;">
                    <div style="color: ${tm.accent}; font-weight: 600; font-size: 0.95rem; display:flex; align-items:center; gap:6px;"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2v6a2 2 0 0 0 .245.96l5.51 10.08A2 2 0 0 1 18 22H6a2 2 0 0 1-1.755-2.96l5.51-10.08A2 2 0 0 0 10 8V2"/><path d="M6.453 15h11.094"/><path d="M8.5 2h7"/></svg>${StringKey.GOAL_FORM_LAB_CTA_LABEL.tr()}</div>
                    <div style="color: ${tm.textSecondary}; font-size: 0.82rem; margin-top: 3px; line-height: 1.35;">${StringKey.GOAL_FORM_LAB_CTA_BODY.tr()}</div>
                </div>
                <div style="margin-bottom: 1.5rem;">
                    <label style="display: block; margin-bottom: 0.5rem; color: ${tm.textSecondary}; font-size: 0.875rem; font-weight: 500;">${StringKey.GOAL_DIALOG_ICON_LABEL.tr()}</label>
                    <input type="hidden" id="goalIconInput" value="$icon0">
                    <div id="iconSelector" style="display: flex; flex-wrap: wrap; gap: 8px;">$iconOptionsHtml</div>
                </div>
                <div style="display: flex; gap: 1rem; justify-content: space-between; align-items: center; margin-top: 1.5rem; padding-top: 1rem; border-top: 1px solid ${tm.border};">
                    $deleteBtnHtml
                    <div style="display:flex; gap: 1rem;">
                        <button id="goalCancelButton" style="background: ${tm.background}; color: ${tm.textSecondary}; border: 1px solid ${tm.border}; padding: 0.75rem 1.5rem; border-radius: 8px; cursor: pointer; font-size: 0.9rem; font-weight: 500;">${StringKey.ACTION_CANCEL.tr()}</button>
                        <button id="goalSaveButton" style="background: $addBtnBg; color: $addBtnFg; border: none; padding: 0.75rem 1.5rem; border-radius: 8px; cursor: pointer; font-size: 0.9rem; font-weight: 600;">$saveLabel</button>
                    </div>
                </div>
            </div>
        """.trimIndent()
        dialog.style.display = "flex"

        val close = { dialog.style.display = "none" }

        // Category dropdown
        dialog.querySelector("#goalCategoryTrigger")?.addEventListener("click", {
            val menu = dialog.querySelector("#goalCategoryMenu") as? HTMLElement
            menu?.style?.display = if (menu?.style?.display == "block") "none" else "block"
        })
        val catOptions = dialog.querySelectorAll(".goal-cat-option")
        for (i in 0 until catOptions.length) {
            (catOptions.item(i) as? HTMLElement)?.addEventListener("click", {
                val value = (it.currentTarget as? HTMLElement)?.getAttribute("data-value") ?: "EMERGENCY_FUND"
                val label = (it.currentTarget as? HTMLElement)?.querySelector("span")?.textContent ?: ""
                val iconId = utils.CategoryIconPaths.goalCategoryIconId[value] ?: "target_arrow"
                (dialog.querySelector("#goalCategorySelect") as? org.w3c.dom.HTMLInputElement)?.value = value
                (dialog.querySelector("#goalCatLabel") as? HTMLElement)?.textContent = label
                (dialog.querySelector("#goalCatIcon") as? HTMLElement)?.innerHTML = utils.CategoryIconPaths.getSvgContent(iconId)
                (dialog.querySelector("#goalCategoryMenu") as? HTMLElement)?.style?.display = "none"
                val newCategory = runCatching { models.GoalCategory.valueOf(value) }.getOrNull()
                val wrapper = dialog.querySelector("#goalAccountWrapper") as? HTMLElement
                if (newCategory != null && wrapper != null) {
                    GlobalScope.launch {
                        val profile = try { ApiService().getStudioProfile() } catch (_: Exception) { null }
                        val defaultAccount = if (profile != null) studio.StudioOrchestrator.defaultAccountFor(newCategory, profile)
                            else models.GoalAccountType.optionsFor(newCategory).firstOrNull()
                        wrapper.innerHTML = renderAccountFieldsForDialog(newCategory, defaultAccount)
                    }
                }
            })
        }

        // Icon selection
        val iconButtons = dialog.querySelectorAll(".icon-option")
        for (i in 0 until iconButtons.length) {
            (iconButtons.item(i) as? HTMLElement)?.addEventListener("click", { event ->
                val icon = (event.currentTarget as? HTMLElement)?.getAttribute("data-icon") ?: icon0
                (dialog.querySelector("#goalIconInput") as? org.w3c.dom.HTMLInputElement)?.value = icon
                for (j in 0 until iconButtons.length) {
                    val other = iconButtons.item(j) as? HTMLElement
                    val on = other?.getAttribute("data-icon") == icon
                    other?.style?.borderColor = if (on) WebTheme.current.accent else WebTheme.current.border
                    other?.style?.background = if (on) "${WebTheme.current.accent}33" else WebTheme.current.background
                }
            })
        }

        dialog.querySelector("#goalCancelButton")?.addEventListener("click", { close() })
        dialog.addEventListener("click", { event -> if (event.target == dialog) close() })

        // Lab CTA — open the Goal Lab, seeded from the current form (create) or
        // the goal being edited.
        dialog.querySelector("#goalFormLabCta")?.addEventListener("click", {
            val state = collectGoalForm(dialog, icon0, requireValid = false)
            close()
            if (isEdit) {
                openLab(container, viewModel, seed = null, existingGoal = existing)
            } else {
                val seed = state?.let {
                    models.SavingsGoal(
                        id = "draft-${kotlin.random.Random.nextInt(100_000, 999_999)}",
                        name = it.name, targetAmount = it.amount, currentAmount = 0.0,
                        deadline = kotlinx.datetime.Clock.System.todayIn(kotlinx.datetime.TimeZone.currentSystemDefault()).plus(kotlinx.datetime.DatePeriod(years = 1)),
                        category = it.category, priority = it.priority,
                        createdDate = kotlinx.datetime.Clock.System.now(), iconEmoji = it.icon, accountType = it.accountType,
                    )
                }
                openLab(container, viewModel, seed = seed, existingGoal = null)
            }
        })

        // Delete (edit only)
        dialog.querySelector("#goalDeleteButton")?.addEventListener("click", {
            if (gateReadOnly()) return@addEventListener
            existing?.let { viewModel.deleteGoal(it.id) }
            close()
        })

        // Save
        dialog.querySelector("#goalSaveButton")?.addEventListener("click", {
            val state = collectGoalForm(dialog, icon0, requireValid = true) ?: return@addEventListener
            if (gateReadOnly()) return@addEventListener
            if (isEdit && existing != null) {
                viewModel.updateGoal(
                    goal = existing, name = state.name, targetAmount = state.amount, deadline = state.deadline,
                    category = state.category, priority = state.priority, iconEmoji = state.icon, accountType = state.accountType,
                )
            } else {
                viewModel.createGoal(
                    name = state.name, category = state.category, targetAmount = state.amount, deadline = state.deadline,
                    iconEmoji = state.icon, priority = state.priority, accountType = state.accountType,
                )
            }
            close()
        })

        window.setTimeout({ (dialog.querySelector("#goalNameInput") as? org.w3c.dom.HTMLInputElement)?.focus() }, 100)
    }

    /** Open the Goal Lab (the scenario explorer, formerly the inline Studio).
     *  Standalone/full-screen. When `existingGoal` is set the Lab commits via
     *  an explicit "Apply to goal" diff; otherwise it saves a new goal. */
    fun openLab(
        container: HTMLElement,
        viewModel: WebSavingsGoalsViewModel,
        seed: models.SavingsGoal? = null,
        existingGoal: models.SavingsGoal? = null,
    ) {
        val today = kotlinx.datetime.Clock.System.todayIn(kotlinx.datetime.TimeZone.currentSystemDefault())
        val goalForLab = existingGoal ?: seed ?: models.SavingsGoal(
            id = "draft-${kotlin.random.Random.nextInt(100_000, 999_999)}",
            name = "", targetAmount = 10_000.0, currentAmount = 0.0,
            deadline = today.plus(kotlinx.datetime.DatePeriod(years = 1)),
            category = models.GoalCategory.EMERGENCY_FUND, priority = models.GoalPriority.NICE_TO_HAVE,
            createdDate = kotlinx.datetime.Clock.System.now(), iconEmoji = "tax",
        )
        GlobalScope.launch {
            val profile = try { ApiService().getStudioProfile() } catch (_: Exception) { null }
            if (profile == null) { showAppAlert(StringKey.STUDIO_NO_PROFILE_BODY.tr()); return@launch }
            WebStudioComponent(
                goal = goalForLab, profile = profile, viewModel = viewModel,
                onPinned = { _ -> }, onDismiss = { },
                isCreating = existingGoal == null,
                labApplyToGoal = existingGoal,
            ).show()
        }
    }

    /** Simple create/edit form for a DEBT_PAYOFF goal — name, balance, rate,
     *  term. The payoff-plan explorer stays in the Debt Studio, reachable via
     *  the Lab CTA. `existing == null` → create. */
    fun showDebtGoalFormDialog(
        container: HTMLElement,
        viewModel: WebSavingsGoalsViewModel,
        existing: models.SavingsGoal? = null,
    ) {
        val tm = WebTheme.current
        val isEdit = existing != null
        var dialog = container.querySelector("#debtGoalFormDialog") as? HTMLElement
        if (dialog == null) {
            dialog = document.createElement("div") as HTMLElement
            dialog.id = "debtGoalFormDialog"
            dialog.style.cssText = "display: none; position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: ${if (tm.isDark) "rgba(0,0,0,0.8)" else "rgba(0,0,0,0.3)"}; z-index: 1000; align-items: center; justify-content: center;"
            container.appendChild(dialog)
        }

        val name0 = existing?.name ?: ""
        val balance0 = existing?.debtRemainingBalance ?: existing?.targetAmount
        val rate0 = existing?.debtInterestRate
        val term0 = existing?.debtRemainingMonths
        val icon0 = existing?.iconEmoji?.takeIf { it.isNotBlank() } ?: "tax"

        val iconIds = utils.CategoryIconPaths.goalIconOptions
        val priorityOptions = listOf(
            "MUST_HAVE" to StringKey.GOAL_PRI_MUST_HAVE.tr(),
            "NICE_TO_HAVE" to StringKey.GOAL_PRI_NICE_TO_HAVE.tr(),
            "STRETCH_GOAL" to StringKey.GOAL_PRI_STRETCH_GOAL.tr(),
            "MAYBE" to StringKey.GOAL_PRI_MAYBE.tr(),
        )
        val priorityOptionsHtml = priorityOptions.joinToString("") { (value, label) ->
            val sel = if (value == existing?.priority?.name || (existing == null && value == "NICE_TO_HAVE")) "selected" else ""
            """<option value="$value" $sel>$label</option>"""
        }
        val iconOptionsHtml = iconIds.joinToString("") { iconId ->
            val svgPaths = utils.CategoryIconPaths.getSvgContent(iconId)
            val on = iconId == icon0
            """<button type="button" class="dicon-option" data-icon="$iconId"
                style="width: 40px; height: 40px; border: 2px solid ${if (on) tm.accent else tm.border}; border-radius: 8px; background: ${if (on) "${tm.accent}33" else tm.background}; cursor: pointer; display: flex; align-items: center; justify-content: center;">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="${tm.textPrimary}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">$svgPaths</svg>
            </button>"""
        }

        val inputBg = tm.background
        val inputBorder = tm.border
        val addBtnBg = if (tm.isDark) "#00d9ff" else tm.accentStrong
        val addBtnFg = if (tm.isDark) "#0a0a0a" else "#ffffff"
        val title = if (isEdit) name0.ifBlank { StringKey.GOAL_TYPE_PICKER_DEBT.tr() } else StringKey.GOAL_TYPE_PICKER_DEBT.tr()
        val saveLabel = if (isEdit) StringKey.STUDIO_SAVE_ACTION.tr() else StringKey.GOAL_DIALOG_CREATE.tr()
        val deleteBtnHtml = if (isEdit) """<button id="debtDeleteButton" style="background: transparent; color: #ff4757; border: 1px solid #ff475766; padding: 0.75rem 1.25rem; border-radius: 8px; cursor: pointer; font-size: 0.9rem; font-weight: 500;">${StringKey.STUDIO_DELETE_GOAL.tr()}</button>""" else "<span></span>"

        fun numField(id: String, label: String, value: String, prefix: String, suffix: String, placeholder: String): String = """
            <div style="margin-bottom: 1.25rem;">
                <label style="display: block; margin-bottom: 0.5rem; color: ${tm.textSecondary}; font-size: 0.875rem; font-weight: 500;">$label</label>
                <div style="position: relative;">
                    ${if (prefix.isNotEmpty()) """<span style="position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: ${tm.textSecondary};">$prefix</span>""" else ""}
                    <input id="$id" type="number" value="$value" placeholder="$placeholder" min="0" step="0.01"
                        style="width: 100%; padding: 0.75rem 0.75rem 0.75rem ${if (prefix.isNotEmpty()) "28px" else "0.75rem"}; border: 1px solid $inputBorder; border-radius: 8px; font-size: 1rem; box-sizing: border-box; background: $inputBg; color: ${tm.textPrimary};">
                    ${if (suffix.isNotEmpty()) """<span style="position: absolute; right: 12px; top: 50%; transform: translateY(-50%); color: ${tm.textSecondary};">$suffix</span>""" else ""}
                </div>
            </div>
        """.trimIndent()

        dialog.innerHTML = """
            <div style="background: ${tm.card}; border-radius: 16px; padding: 2rem; max-width: 500px; width: 90%; max-height: 85vh; overflow-y: auto; border: 1px solid ${tm.border}; box-shadow: ${if (tm.isDark) "none" else "0 20px 60px rgba(0,0,0,0.15)"};">
                <h2 style="margin: 0 0 1.5rem 0; color: ${tm.textPrimary}; font-size: 1.5rem; font-weight: 600;">$title</h2>
                <div style="margin-bottom: 1.25rem;">
                    <label style="display: block; margin-bottom: 0.5rem; color: ${tm.textSecondary}; font-size: 0.875rem; font-weight: 500;">${StringKey.DEBT_STUDIO_GOAL_NAME_LABEL.tr()}</label>
                    <input id="debtNameInput" type="text" value="$name0" placeholder="${StringKey.GOAL_DIALOG_NAME_PLACEHOLDER.tr()}"
                        style="width: 100%; padding: 0.75rem; border: 1px solid $inputBorder; border-radius: 8px; font-size: 1rem; box-sizing: border-box; background: $inputBg; color: ${tm.textPrimary};">
                </div>
                ${numField("debtBalanceInput", StringKey.DEBT_STUDIO_GOAL_BALANCE_LABEL.tr(), balance0?.let { if (it % 1.0 == 0.0) it.toInt().toString() else it.toString() } ?: "", "$", "", "10000")}
                ${numField("debtRateInput", StringKey.DEBT_STUDIO_GOAL_RATE_LABEL.tr(), rate0?.toString() ?: "", "", "%", "5.0")}
                ${numField("debtTermInput", StringKey.DEBT_STUDIO_GOAL_TERM_LABEL.tr(), term0?.toString() ?: "", "", "", "60")}
                <div style="margin-bottom: 1.25rem;">
                    <label style="display: block; margin-bottom: 0.5rem; color: ${tm.textSecondary}; font-size: 0.875rem; font-weight: 500;">${StringKey.GOAL_DIALOG_PRIORITY_LABEL.tr()}</label>
                    <select id="debtPrioritySelect" style="width: 100%; padding: 0.75rem; border: 1px solid $inputBorder; border-radius: 8px; font-size: 1rem; box-sizing: border-box; background: $inputBg; color: ${tm.textPrimary}; cursor: pointer;">$priorityOptionsHtml</select>
                </div>
                <div id="debtFormLabCta" style="margin-bottom: 1.25rem; padding: 0.9rem 1rem; border: 1px dashed ${tm.accent}; border-radius: 10px; background: ${tm.accent}12; cursor: pointer;">
                    <div style="color: ${tm.accent}; font-weight: 600; font-size: 0.95rem; display:flex; align-items:center; gap:6px;"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2v6a2 2 0 0 0 .245.96l5.51 10.08A2 2 0 0 1 18 22H6a2 2 0 0 1-1.755-2.96l5.51-10.08A2 2 0 0 0 10 8V2"/><path d="M6.453 15h11.094"/><path d="M8.5 2h7"/></svg>${StringKey.GOAL_FORM_LAB_CTA_LABEL.tr()}</div>
                    <div style="color: ${tm.textSecondary}; font-size: 0.82rem; margin-top: 3px; line-height: 1.35;">${StringKey.GOAL_FORM_LAB_CTA_BODY.tr()}</div>
                </div>
                <div style="margin-bottom: 1.5rem;">
                    <label style="display: block; margin-bottom: 0.5rem; color: ${tm.textSecondary}; font-size: 0.875rem; font-weight: 500;">${StringKey.GOAL_DIALOG_ICON_LABEL.tr()}</label>
                    <input type="hidden" id="debtIconInput" value="$icon0">
                    <div style="display: flex; flex-wrap: wrap; gap: 8px;">$iconOptionsHtml</div>
                </div>
                <div style="display: flex; gap: 1rem; justify-content: space-between; align-items: center; margin-top: 1.5rem; padding-top: 1rem; border-top: 1px solid ${tm.border};">
                    $deleteBtnHtml
                    <div style="display:flex; gap: 1rem;">
                        <button id="debtCancelButton" style="background: ${tm.background}; color: ${tm.textSecondary}; border: 1px solid ${tm.border}; padding: 0.75rem 1.5rem; border-radius: 8px; cursor: pointer; font-size: 0.9rem; font-weight: 500;">${StringKey.ACTION_CANCEL.tr()}</button>
                        <button id="debtSaveButton" style="background: $addBtnBg; color: $addBtnFg; border: none; padding: 0.75rem 1.5rem; border-radius: 8px; cursor: pointer; font-size: 0.9rem; font-weight: 600;">$saveLabel</button>
                    </div>
                </div>
            </div>
        """.trimIndent()
        dialog.style.display = "flex"

        val close = { dialog.style.display = "none" }

        val iconButtons = dialog.querySelectorAll(".dicon-option")
        for (i in 0 until iconButtons.length) {
            (iconButtons.item(i) as? HTMLElement)?.addEventListener("click", { event ->
                val icon = (event.currentTarget as? HTMLElement)?.getAttribute("data-icon") ?: icon0
                (dialog.querySelector("#debtIconInput") as? org.w3c.dom.HTMLInputElement)?.value = icon
                for (j in 0 until iconButtons.length) {
                    val other = iconButtons.item(j) as? HTMLElement
                    val on = other?.getAttribute("data-icon") == icon
                    other?.style?.borderColor = if (on) WebTheme.current.accent else WebTheme.current.border
                    other?.style?.background = if (on) "${WebTheme.current.accent}33" else WebTheme.current.background
                }
            })
        }

        dialog.querySelector("#debtCancelButton")?.addEventListener("click", { close() })
        dialog.addEventListener("click", { event -> if (event.target == dialog) close() })
        dialog.querySelector("#debtDeleteButton")?.addEventListener("click", {
            if (gateReadOnly()) return@addEventListener
            existing?.let { viewModel.deleteGoal(it.id) }
            close()
        })
        dialog.querySelector("#debtFormLabCta")?.addEventListener("click", {
            close()
            openDebtLab(container, viewModel, existing)
        })

        dialog.querySelector("#debtSaveButton")?.addEventListener("click", {
            val nameInput = dialog.querySelector("#debtNameInput") as? org.w3c.dom.HTMLInputElement
            val balanceInput = dialog.querySelector("#debtBalanceInput") as? org.w3c.dom.HTMLInputElement
            val name = nameInput?.value?.trim() ?: ""
            val balance = balanceInput?.value?.toDoubleOrNull() ?: 0.0
            val rate = (dialog.querySelector("#debtRateInput") as? org.w3c.dom.HTMLInputElement)?.value?.toDoubleOrNull()
            val term = (dialog.querySelector("#debtTermInput") as? org.w3c.dom.HTMLInputElement)?.value?.toIntOrNull()
            val icon = (dialog.querySelector("#debtIconInput") as? org.w3c.dom.HTMLInputElement)?.value ?: icon0
            val priority = runCatching { models.GoalPriority.valueOf((dialog.querySelector("#debtPrioritySelect") as? org.w3c.dom.HTMLSelectElement)?.value ?: "NICE_TO_HAVE") }.getOrDefault(models.GoalPriority.NICE_TO_HAVE)
            if (name.isEmpty()) { nameInput?.style?.borderColor = "#ff4757"; return@addEventListener }
            if (balance <= 0) { balanceInput?.style?.borderColor = "#ff4757"; return@addEventListener }
            if (gateReadOnly()) return@addEventListener

            val today = kotlinx.datetime.Clock.System.todayIn(kotlinx.datetime.TimeZone.currentSystemDefault())
            val deadlineLocal = if (term != null && term > 0) today.plus(kotlinx.datetime.DatePeriod(months = term)) else today.plus(kotlinx.datetime.DatePeriod(years = 5))
            val deadlineJs = Date("${deadlineLocal.year}-${deadlineLocal.monthNumber.toString().padStart(2, '0')}-${deadlineLocal.dayOfMonth.toString().padStart(2, '0')}")

            if (isEdit && existing != null) {
                viewModel.updateGoal(
                    goal = existing, name = name, targetAmount = balance, deadline = deadlineJs,
                    category = models.GoalCategory.DEBT_PAYOFF, priority = priority, iconEmoji = icon, accountType = null,
                    debtInterestRate = rate, debtRemainingBalance = balance, debtRemainingMonths = term,
                )
                close()
            } else {
                viewModel.createGoal(
                    name = name, category = models.GoalCategory.DEBT_PAYOFF, targetAmount = balance, deadline = deadlineJs,
                    iconEmoji = icon, priority = priority, accountType = null,
                    onCreated = { created ->
                        viewModel.updateGoal(
                            goal = created, name = name, targetAmount = balance, deadline = deadlineJs,
                            category = models.GoalCategory.DEBT_PAYOFF, priority = priority, iconEmoji = icon, accountType = null,
                            debtInterestRate = rate, debtRemainingBalance = balance, debtRemainingMonths = term,
                        )
                        close()
                    },
                )
            }
        })

        window.setTimeout({ (dialog.querySelector("#debtNameInput") as? org.w3c.dom.HTMLInputElement)?.focus() }, 100)
    }

    /** Open the Debt Studio (the payoff-plan explorer) as the debt Goal Lab. */
    fun openDebtLab(
        container: HTMLElement,
        viewModel: WebSavingsGoalsViewModel,
        existingGoal: models.SavingsGoal? = null,
    ) {
        val today = kotlinx.datetime.Clock.System.todayIn(kotlinx.datetime.TimeZone.currentSystemDefault())
        val goalForLab = existingGoal ?: models.SavingsGoal(
            id = "draft-${kotlin.random.Random.nextInt(100_000, 999_999)}",
            name = "", targetAmount = 0.0, currentAmount = 0.0,
            deadline = today.plus(kotlinx.datetime.DatePeriod(years = 5)),
            category = models.GoalCategory.DEBT_PAYOFF, priority = models.GoalPriority.NICE_TO_HAVE,
            createdDate = kotlinx.datetime.Clock.System.now(), iconEmoji = "tax",
            debtRemainingBalance = 0.0, debtInterestRate = 0.0, debtRemainingMonths = null,
        )
        WebDebtStudioComponent(
            goal = goalForLab, viewModel = viewModel, onDismiss = { }, isCreating = existingGoal == null,
            labApplyToGoal = existingGoal,
        ).show()
    }
}

